import PaymentVisitDetails from './PaymentVisitDetails';
import {paymentMethodDetailsError,paymentModeDescription,paymentReferenceDisplay} from './payment-method-details';
import PaymentMethodSelector from './PaymentMethodSelector';
import BillDraftEditor from './BillDraftEditor';
import {LegacyReconciliationDrawer} from './LegacyReconciliationDrawer';
import {pendingReconciliation,type ReconciliationIntent} from './reconciliation-recovery';
import {moneyOperationSession} from '../money/money-operation-session';
import { BillCollectionReview, useBillCollectionReview } from './BillCollectionReview';
import { pendingBillMutationId, retryPendingBillMutation, pendingPaymentVoidId, retryPendingPaymentVoid } from './bill-mutation-recovery';
import { ConsultationAttribution, type ConsultationActivityDisplay } from './consultation-display';
import AcBadge from '@/dashboard/ui/AcBadge';
/**
 * /dashboard/billing-entry -- the smallest honest payment-entry form
 * (PATIENT-PAYMENTS-REVENUE-SPINE-V1, Epic 9 rung 2).
 *
 * Composition, not construction: the patient is PICKED via the existing
 * patient-master search (never typed free-text -- deterministic money needs
 * a deterministic identity), the clinic list and category vocabulary come
 * from the org-fenced context read, and every write goes through the ONE
 * writer's routes. Corrections are VOID + re-enter (append-only M) --
 * there is deliberately no edit button anywhere on this page.
 *
 * Honest states: while the patient-master ladder is dark
 * (PATIENT_MASTER_V1_ENABLED off) the search returns nothing / fails --
 * the page says so plainly instead of pretending. Sparse data renders as
 * sparse data (no fabricated rows).
 *
 * Visit link: the API and spine fully support an optional appointmentId
 * (org/clinic-fenced); v1 omits the picker UI because no per-patient
 * appointment-id read exists in the web stack yet (recorded gap).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  cancelPatientBillApi,
  createPatientBillApi,
  fetchBillDoctorOptions,
  fetchBillingEntryContext,
  fetchBillingQueue,
  fetchCheckoutHandoff,
  fetchPatientBills,
  fetchPatientReceipts,
  fetchPatientMasterRecord,
  fetchRecentPatientPayments,
  savePatientBillDraftApi,
  getCaseSheetByAppointment,
  recordCaseSheetDiscount,
  recordPatientPaymentApi,
  voidPatientPaymentApi,
  voidTreatmentDiscountApi,
  fetchPatientMasterSearch,
} from '@/dashboard/api';
import { AllocationRecoveryPanel, useAllocationRecovery } from './payment-allocation-recovery';
import ConsultationActions from './ConsultationActions';
import { useConsultationIdentity } from './use-consultation-identity';
import type { ConsultationBillState } from '@/dashboard/api';
import { authBearer, safeErrorMessage, PortalApiError } from '@/lib/portal-api';
import { clinicLabelOf, useClinicLens } from '@/dashboard/clinic-context';
import { useNarrowViewport } from '@/dashboard/coc/use-narrow-viewport';
import ClinicScopeLabel from '@/dashboard/ui/ClinicScopeLabel';
import { useDashboardMeContext } from '@/dashboard/me-context';
import { getSupabaseBrowserClient } from '@/lib/supabase';
import {
  AcButton,
  EmptyState,
  ErrorState,
  LoadingState,
  NextActionBar,
  PageHeader,
  acButtonClass,
} from '@/dashboard/ui';
import { PAYMENT_RECORDED_NEXT_ACTION_LABEL } from '@/dashboard/appointments/workflow-handoff';
import ClinicLinksBar from '@/dashboard/clinic-links/ClinicLinksBar';
// SMART-PAYMENT-DRAWER-V1. NOTE the deliberate module cycle: the drawer
// renders THIS module's exported BillingReceiptPanel (the package's
// reuse-before-build law -- one receipt, one home), and this page mounts the
// drawer. Safe by construction: both sides are function components touched
// only at RENDER time, never during module initialisation.
import PaymentDrawer from '@/dashboard/billing/PaymentDrawer';
import ReceiptPrintView from '@/dashboard/billing/ReceiptPrintView';
import ModalShell from '@/dashboard/ui/ModalShell';
import MasterDetail from '@/dashboard/ui/MasterDetail';
// BILLING-MISSING-DELTA-V1: the itemized appointment bill (the tracker's
// "We need these particulars in billing" cluster). ONE render of lines +
// totals (BillDetailCard) shared with the COC read and the print surface;
// the editor lives in THIS file (its select/input grammar is this page's
// existing form grammar).
import BillDetailCard from '@/dashboard/billing/BillDetailCard';
import CollectPaymentSummary from '@/dashboard/billing/CollectPaymentSummary';
// COLLECT-PAYMENT-V1 -- the composition's own rules, LOADED (not injected).
// The repo's shipped pattern for a surface with its own CSS is a stylesheet
// beside the surface, imported by it (callcenter-workspace.css,
// doctor-cockpit-v2.css, consult-plan-row.css). dashboard.css stays Factory's.
import '@/dashboard/billing/collect-payment.css';
import BillPrintView from '@/dashboard/billing/BillPrintView';
import {
  BILL_CANCEL_REASON_OPTIONS,
  isBillStoreUnavailableError,
  readBillDoctorOptionsWire,
  billListMatchesScope,
  readPatientBillListWire,
  type BillDoctorOption,
  type PatientBillWireMirror,
} from '@/dashboard/billing/bill-view';
import type { DashboardMe } from '@/dashboard/types';
import {
  deskRecordHref,
  type PatientCandidateCard,
} from '@/dashboard/patients/patient-master-view';
import { formatTimestampIst } from '@/dashboard/owner/Lead360Panel';
import {
  ACCEPTED_AMOUNT_NOT_CONFIGURED_COPY,
  categoryLabel,
  clinicOptionLabel,
  episodeStateLabel,
  formatMinorUnitsInr,
  parseInrToMinorUnits,
  nextLinkedAppointment,
  queueAgeLabel,
  walkinFeeInputValue,
  BILLING_LINK_UNALLOCATED_LINE,
  PAYMENT_METHOD_OPTIONS,
  VOID_REASON_OPTIONS,
  type BillingEntryContext,
  type BillingQueueResponse,
  type BillingQueueRow,
  type PatientPaymentRow,
  referenceNumberForWire,
} from '@/dashboard/billing/billing-entry-view';
import {
  CHECKOUT_ACTION_REASON_COPY,
  CHECKOUT_BALANCE_COPY,
  CHECKOUT_BOUNDARY_COPY,
  CHECKOUT_PRIMARY_ACTION_LABELS,
  COMMERCIAL_CONVERSION_LABELS,
  checkoutFactLabels,
  COURSE_COLLECTED_COPY,
  COURSE_COMPLETION_UNAVAILABLE_COPY,
  COURSE_CONTEXT_UNAVAILABLE_COPY,
  COURSE_LEDGER_STATE_LABELS,
  COURSE_REMAINING_COPY,
  COURSE_TOTAL_COPY,
  EMPTY_DISCOUNT_FORM,
  FOLLOW_UP_LABELS,
  NEXT_APPOINTMENT_LABELS,
  PAYMENT_EVIDENCE_LABELS,
  PRICE_UNAVAILABLE_COPY,
  PROPOSED_PAYMENT_STRUCTURE_LABELS,
  checkoutPrimaryActionHref,
  discountApproverRoleLabel,
  discountReasonLabel,
  validateDiscountForm,
  type CheckoutHandoffResponse,
  type DiscountFormState,
} from '@/dashboard/billing/checkout-handoff-view';

interface Feedback {
  readonly kind: 'success' | 'error';
  readonly text: string;
}

function FeedbackBanner({ feedback }: { feedback: Feedback | null }): JSX.Element | null {
  if (feedback === null) return null;
  return (
    <div
      className={
        feedback.kind === 'success'
          ? 'ac-banner ac-banner--good'
          : 'ac-banner ac-banner--danger'
      }
      role="status"
      aria-live="polite"
    >
      {feedback.text}
    </div>
  );
}

interface SelectedPatient {
  readonly patientId: string;
  readonly fullName: string;
  readonly umr: string;
}

/**
 * TRAIN-B SCOPE 5 (payment-evidence link): the day board's "Record payment"
 * door arrives with ?appointmentId=N&patientId=U&clinicId=C. Parsed once,
 * window-guarded (SSR test renders keep the manual flow). The appointment
 * link exercises the ONE payment writer's EXISTING optional appointmentId
 * input (org/clinic-fenced server-side) - closing the #198 recorded gap
 * with ZERO write-path change.
 */
export interface BillingPrefill {
  readonly appointmentId: number | null;
  readonly patientId: string | null;
  readonly clinicId: number | null;
  /** CHECKOUT-HANDOFF-AND-DOCTOR-CONTEXT-V1 (HANDOFF LAW): the whole-INR
   *  suggestion from the doctor's ACCEPTED plan (the Collect CTA's carry).
   *  Seeds the editable amount input ONCE; never a write input by itself
   *  (parseInrToMinorUnits at submit stays the ONE converter). */
  readonly suggestedAmount: number | null;
}

export function billingPrefillFromSearch(search: string): BillingPrefill {
  const p = new URLSearchParams(search);
  const num = (k: string): number | null => {
    const v = p.get(k);
    return v !== null && /^\d+$/.test(v) ? Number(v) : null;
  };
  const pid = p.get('patientId');
  const suggested = num('suggestedAmount');
  return {
    appointmentId: num('appointmentId'),
    patientId: pid !== null && pid.trim() !== '' ? pid.trim() : null,
    clinicId: num('clinicId'),
    // Bounded sanity: a crafted/absurd value never seeds the form.
    suggestedAmount:
      suggested !== null && suggested > 0 && suggested <= 100_000_000
        ? suggested
        : null,
  };
}

function billingPrefillFromUrl(): BillingPrefill {
  if (typeof window === 'undefined') {
    return {
      appointmentId: null,
      patientId: null,
      clinicId: null,
      suggestedAmount: null,
    };
  }
  return billingPrefillFromSearch(window.location.search);
}

/**
 * DOCTOR-CLINICAL-WORKSPACE-V1 (Scope 6): the treatment-advised value from
 * the visit's case sheet, surfaced on the payment door as DISPLAY-ONLY
 * context (never a write input; the billing queue stays Train F). The
 * by-appointment read is ROLE-SAFE server-side -- the receptionist receives
 * the billing view (procedure + value + status only) -- so this extractor
 * defensively accepts any audience shape and reads only the billing-safe
 * fields. Declined / pending items are excluded (an advised-or-accepted
 * procedure is the honest payment expectation).
 */
export interface AdvisedPlanDisplay {
  readonly items: ReadonlyArray<{
    readonly name: string;
    readonly value: number | null;
  }>;
  readonly total: number;
}

export function advisedPlanFromCaseSheet(sheet: unknown): AdvisedPlanDisplay | null {
  if (sheet === null || typeof sheet !== 'object') return null;
  const planItems = (sheet as { planItems?: unknown }).planItems;
  if (!Array.isArray(planItems)) return null;
  const items: Array<{ name: string; value: number | null }> = [];
  let total = 0;
  for (const raw of planItems) {
    if (raw === null || typeof raw !== 'object') continue;
    const it = raw as {
      procedureName?: unknown;
      estimatedValue?: unknown;
      status?: unknown;
    };
    if (typeof it.procedureName !== 'string' || it.procedureName === '') continue;
    if (it.status !== 'advised' && it.status !== 'accepted') continue;
    const value =
      typeof it.estimatedValue === 'number' && Number.isFinite(it.estimatedValue)
        ? it.estimatedValue
        : null;
    items.push({ name: it.procedureName, value });
    if (value !== null) total += value;
  }
  return items.length === 0 ? null : { items, total };
}

export interface RecentPaymentsTableProps {
  readonly onReconcile?: ((payment:PatientPaymentRow)=>void)|undefined;
  readonly reconciliationByClinic?: Readonly<Record<string,boolean>>|undefined;
  readonly onOpenReceipt?: (payment: PatientPaymentRow) => void;
  readonly openingReceiptId?: number | null;
  /** Identity already read from the selected patient record; never matched by name. */
  readonly selectedPatientIdentity?: Pick<SelectedPatient, 'patientId' | 'umr'> | null;
  readonly readableMethodLabels?: boolean;
  readonly payments: readonly PatientPaymentRow[];
  readonly canVoid: boolean;
  readonly voidingId: number | null;
  readonly onStartVoid: (id: number) => void;
  readonly onConfirmVoid: (id: number, reason: string) => void;
  readonly onCancelVoid: () => void;
  /**
   * FRONT-DESK-MONEY-AND-DOCUMENTS-V1 (PR3, L-07) -- THE RE-LINK.
   *
   * The visit this operator is currently standing on, or null when she
   * arrived queue-less. The re-link control renders ONLY when there IS such
   * a visit AND the row is unallocated AND not voided: a door that cannot
   * name the visit it would attach money to is not offered at all.
   */
  readonly relinkAppointmentId?: number | null;
  /** null = no re-link door on this list (every pre-package caller). */
  readonly onRelink?: ((paymentId: number) => void) | null;
  /** The row whose re-link is in flight (its control disables). */
  readonly relinkingId?: number | null;
  /**
   * THE UNDO's own permission, and it is NOT the re-link's.
   *
   * Attaching money is the entry role's act; UNDOING a committed allocation
   * is a correction, and corrections are supervisory here. So the undo
   * control is STRUCTURALLY ABSENT for a principal who could not pass the
   * endpoint's gate -- never visible-but-disabled, and never a control that
   * leads to a refusal. Wired from the SAME `canVoid` the wire already
   * serves, because the endpoint reuses `requireBillingVoidAccess`.
   */
  readonly onUnlink?: ((paymentId: number, appointmentId: number) => void) | null;
  readonly unlinkingId?: number | null;
  readonly preserveAttachmentStatus?: boolean;
}

/** The re-link door's label and its refusal copy -- ONE home each. */
export const BILLING_RELINK_LABEL = 'Attach to this visit';
export const BILLING_RELINK_HINT =
  'This payment is not attached to any visit, so it does not reduce any due.';

/** The UNDO's label and hint. It says ATTACHMENT, never "void": undoing an
 *  allocation must not read as undoing the collection. */
export const BILLING_UNLINK_LABEL = 'Remove from visit';
export const BILLING_UNLINK_HINT =
  'Detach this payment from the visit. The payment stays active and collected; only the visit link is removed.';

/** Pure presentational recent-payments list - testable via react-dom/server. */
/**
 * FRONT-DESK-CORRECTIVE-V1 (L-07 + L-02, scope 1A) -- THE VISIT-LINK STATE
 * LINE. The founder invariant this carries, verbatim: "the operator must know
 * where the money is going before Save."
 *
 * EXTRACTED VERBATIM from the inline JSX it replaces -- the LINKED string is
 * byte-identical to the one that shipped, including "(from the day board)"
 * and the "Remove link" act. This component adds the branch that was MISSING,
 * never a second grammar for the branch that already worked.
 */
export function BillingVisitLinkNote({
  linkedAppointmentId,
  onRemoveLink,
}: {
  readonly linkedAppointmentId: number | null;
  readonly onRemoveLink: () => void;
}): JSX.Element | null {
  if (linkedAppointmentId === null) {
    // THE BRANCH THAT WAS MISSING. At HEAD this rendered NOTHING AT ALL --
    // the unallocated state was not mislabelled, it was SILENT.
    return (
      <p
        className="dashboard-page__note"
        data-testid="billing-visit-link-unallocated"
      >
        <strong>{BILLING_LINK_UNALLOCATED_LINE}</strong>. Arrive through a
        visit&apos;s Collect door to record against that visit.
      </p>
    );
  }
  return (
    <p className="dashboard-page__note" data-testid="billing-visit-link">
      Recording against <strong>appointment #{linkedAppointmentId}</strong>{' '}
      (from the day board) - the payment will carry this visit link.{' '}
      <AcButton
        variant="ghost"
        size="sm"
        onClick={onRemoveLink}
        data-testid="billing-visit-link-remove"
      >
        Remove link
      </AcButton>
    </p>
  );
}

export function RecentPaymentsTable({
  onReconcile, reconciliationByClinic,
  onOpenReceipt,
  openingReceiptId = null,
  selectedPatientIdentity = null,
  payments,
  canVoid,
  voidingId,
  onStartVoid,
  onConfirmVoid,
  onCancelVoid,
  relinkAppointmentId = null,
  onRelink = null,
  relinkingId = null,
  onUnlink = null,
  unlinkingId = null,
  preserveAttachmentStatus = false,
}: RecentPaymentsTableProps): JSX.Element {
  // THE DOOR IS OFFERED ONLY WHEN IT CAN NAME ITS VISIT (L-07). With no
  // armed appointment there is nothing to attach to, so the column is
  // STRUCTURALLY ABSENT -- never a disabled button, never a picker this
  // package did not earn.
  const relinkOffered = onRelink !== null && relinkAppointmentId !== null;
  // The undo needs no armed visit -- it removes the link the ROW carries.
  const unlinkOffered = onUnlink !== null;
  const visitColumnOffered = relinkOffered || unlinkOffered || preserveAttachmentStatus;
  if (payments.length === 0) {
    // V7-P4 adoption: the shared EmptyState (03 P11), copy unchanged.
    return (
      <EmptyState
        title="No payments recorded at this clinic yet."
        testId="billing-recent-empty"
      />
    );
  }
  return (
    <table className="table ac-table billing-recent-table" data-testid="billing-recent-table">
      <thead>
        <tr>
          <th>Received</th>
          <th>Patient</th>
          <th>Amount</th>
          <th>Method</th>
          <th>Category</th>
          {/* L-03: the reference belongs BESIDE the mode it reconciles. A
              row with none reads "-" through the SAME not-recorded idiom
              methodLabel/categoryLabel already use -- never a blank cell. */}
          <th>Reference</th>
          <th>Status</th>
          {onOpenReceipt ? <th>Receipt</th> : null}
          {onReconcile ? <th>Allocations</th> : null}
          {visitColumnOffered ? <th>Visit</th> : null}
          {canVoid ? <th>Action</th> : null}
        </tr>
      </thead>
      <tbody>
        {payments.map((p) => {
          const voided = p.voidedAt !== null;
          return (
            <tr key={p.id} data-testid={`billing-payment-${p.id}`}>
              <td>{formatTimestampIst(p.occurredAt)}</td>
              <td>
                {voided ? <s>{p.patientName ?? p.patientId}</s> : (p.patientName ?? p.patientId)}
                {selectedPatientIdentity?.patientId === p.patientId && selectedPatientIdentity.umr !== '' ? (
                  <span className="ac-collect__umr" data-testid={`billing-payment-umr-${p.id}`}>
                    {' '}{selectedPatientIdentity.umr}
                  </span>
                ) : null}
              </td>
              <td>
                {voided ? (
                  <s>{formatMinorUnitsInr(p.amountMinorUnits)}</s>
                ) : (
                  formatMinorUnitsInr(p.amountMinorUnits)
                )}
              </td>
              <td>{p.method ? paymentModeDescription(p) : '\u2014'}</td>
              <td>{categoryLabel(p.category)}</td>
              <td data-testid={`billing-payment-reference-${p.id}`}>
                {paymentReferenceDisplay(p)}
              </td>
              <td>
                {voided ? `VOIDED (${p.voidReason ?? 'other'})` : 'Recorded'}
                {voided &&
                p.voidedByLabel !== undefined &&
                p.voidedByLabel !== null ? (
                  <span data-testid={`billing-payment-voided-by-${p.id}`}>
                    {' '}
                    - by {p.voidedByLabel}
                  </span>
                ) : null}
              </td>
              {onOpenReceipt ? <td><AcButton variant="ghost" size="sm"
                data-testid={`billing-open-receipt-${p.id}`}
                disabled={openingReceiptId !== null} onClick={() => onOpenReceipt(p)}>
                {openingReceiptId === p.id ? 'Opening receipt...' : 'Open receipt'}
              </AcButton></td> : null}
              {onReconcile ? <td>{!voided && reconciliationByClinic?.[String(p.clinicId)]===true ? <AcButton variant="ghost" size="sm" onClick={()=>onReconcile(p)}>Review allocations</AcButton> : null}</td> : null}
              {visitColumnOffered ? (
                <td>
                  {preserveAttachmentStatus && !voided ? <span>Last confirmed: </span> : null}
                  {voided ? null : p.appointmentId !== null ? (
                    <span data-testid={`billing-payment-visit-${p.id}`}>
                      Appointment #{p.appointmentId}
                      {unlinkOffered ? (
                        <>
                          {' '}
                          <AcButton
                            variant="ghost"
                            size="sm"
                            disabled={unlinkingId === p.id}
                            onClick={() =>
                              onUnlink?.(p.id, p.appointmentId as number)
                            }
                            data-testid={`billing-payment-unlink-${p.id}`}
                            style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                            title={BILLING_UNLINK_HINT}
                          >
                            {unlinkingId === p.id
                              ? 'Undoing...'
                              : BILLING_UNLINK_LABEL}
                          </AcButton>
                        </>
                      ) : null}
                    </span>
                  ) : relinkOffered ? (
                    <span data-testid={`billing-payment-unallocated-${p.id}`}>
                      <AcButton
                        variant="secondary"
                        size="sm"
                        disabled={relinkingId === p.id}
                        onClick={() => onRelink?.(p.id)}
                        data-testid={`billing-payment-relink-${p.id}`}
                        style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                        title={BILLING_RELINK_HINT}
                      >
                        {relinkingId === p.id
                          ? 'Attaching...'
                          : BILLING_RELINK_LABEL}
                      </AcButton>
                    </span>
                  ) : <span>not attached to any visit</span>}
                </td>
              ) : null}
              {canVoid ? (
                <td>
                  {voided ? null : voidingId === p.id ? (
                    <span>
                      {VOID_REASON_OPTIONS.map((reason) => (
                        <AcButton
                          key={reason}
                          variant="secondary"
                          size="sm"
                          onClick={() => onConfirmVoid(p.id, reason)}
                        >
                          {reason.replace(/_/g, ' ')}
                        </AcButton>
                      ))}
                      <AcButton variant="ghost" size="sm" onClick={onCancelVoid}>
                        cancel
                      </AcButton>
                    </span>
                  ) : (
                    <AcButton
                      variant="secondary"
                      size="sm"
                      onClick={() => onStartVoid(p.id)}
                    >
                      void
                    </AcButton>
                  )}
                </td>
              ) : null}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ---- The billing queue (MONEY-COMPLETION-V1, V3-P3; rider R-10) -----------

/** The deep-work fallback beside the in-place drawer door on a queue row
 *  (SMART-PAYMENT-DRAWER-V1). */
export const QUEUE_FULL_FORM_COPY = 'Full form';

/** The clinic-wide queue section's testid, exported so a test binds to repo
 *  truth rather than a copy of the string (FRONT-DESK-CORRECTIVE-V1 L-02). */
export const BILLING_ENTRY_QUEUE_TESTID = 'billing-queue';

export interface BillingQueueSectionProps {
  readonly state:
    | { kind: 'loading' }
    | { kind: 'error' }
    | { kind: 'ready'; data: BillingQueueResponse };
  /** Arm the convert-moment card + prefill from ONE queue row (the door --
   *  the row's own episode, never re-picked). */
  readonly onCollect: (row: BillingQueueRow) => void;
  /**
   * SMART-PAYMENT-DRAWER-V1 (Refs #315): when supplied, the row's PRIMARY
   * Collect action opens the in-place drawer, and `onCollect` above stays
   * beside it as the explicit deep-work fallback (the full page form, with
   * search, history, voids, unlinked payments, split and complex cases).
   * ABSENT = today's behaviour, byte-identical -- every pre-package pin
   * renders unchanged.
   */
  readonly onOpenDrawer?: (row: BillingQueueRow) => void;
}

/** Pure presentational queue - testable via react-dom/server. FIELDS, NOT
 *  SENTENCES: every fact is a labeled column. The list displays wire rows
 *  only; the netting lives server-side (the evidence-list law). */
export function BillingQueueSection({
  state,
  onCollect,
  onOpenDrawer,
}: BillingQueueSectionProps): JSX.Element {
  return (
    <section aria-label="Billing queue" data-testid="billing-queue">
      <h2>Billing queue - accepted, not yet collected</h2>
      <p className="dashboard-page__note">
        Every closed visit whose accepted treatment still has money
        outstanding, across days, for your clinics. Partial payments reduce
        the outstanding; voided payments never count. Amounts are AdoraCare
        recorded-collections evidence - the clinic billing system remains
        the book of record.
      </p>
      {state.kind === 'loading' ? (
        // V7-P4 adoption: the shared LoadingState (03 P11), copy unchanged.
        <LoadingState label="Loading the billing queue..." />
      ) : null}
      {state.kind === 'error' ? (
        // V7-P4 adoption: the shared ErrorState (03 P11 S4), copy unchanged.
        <ErrorState
          title="The billing queue could not load right now. Recording payments below still works."
          testId="billing-queue-error"
        />
      ) : null}
      {state.kind === 'ready' ? (
        state.data.rows.length === 0 ? (
          // V7-P4 adoption: the shared EmptyState (03 P11), copy unchanged.
          <EmptyState
            title="Nothing waiting - every closed visit with accepted treatment is collected (or none exists yet)."
            testId="billing-queue-empty"
          />
        ) : (
          <>
            {/* NULL-AMOUNT LAW at the SUMMARY grain (adversarial review
                C2): the numeric total speaks ONLY for the visits whose
                amounts are configured -- a queue of unknown-size
                obligations NEVER reads "INR 0 outstanding", and a
                not-configured visit is never counted inside the numeric
                visit count AND again in the disclosure. */}
            <p
              className="dashboard-page__note"
              data-testid="billing-queue-summary"
            >
              {(() => {
                const s = state.data.summary;
                const numericVisits = s.rowCount - s.notConfiguredRowCount;
                const notConfiguredPhrase =
                  s.notConfiguredRowCount > 0
                    ? `${s.notConfiguredRowCount} ${
                        s.notConfiguredRowCount === 1 ? 'visit' : 'visits'
                      } without a configured amount (no number to total)`
                    : null;
                const cappedPhrase = s.capped
                  ? ' - partial view: more accepted items exist than one scan reads'
                  : '';
                if (numericVisits <= 0) {
                  return (
                    <>
                      <strong>{notConfiguredPhrase}</strong>
                      {cappedPhrase}
                    </>
                  );
                }
                return (
                  <>
                    <strong>
                      {formatMinorUnitsInr(s.outstandingTotalMinorUnits)}{' '}
                      outstanding
                    </strong>{' '}
                    across {numericVisits}{' '}
                    {numericVisits === 1 ? 'visit' : 'visits'}
                    {notConfiguredPhrase ? ` + ${notConfiguredPhrase}` : ''}
                    {cappedPhrase}
                  </>
                );
              })()}
            </p>
            <table className="table" data-testid="billing-queue-table">
              <thead>
                <tr>
                  <th>Patient</th>
                  <th>Visit</th>
                  <th>Accepted</th>
                  <th>Collected</th>
                  <th>Outstanding</th>
                  <th>Age</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {state.data.rows.map((row) => (
                  <tr
                    key={row.appointmentId}
                    data-testid={`billing-queue-row-${row.appointmentId}`}
                  >
                    <td>{row.patientLabel ?? 'Name not recorded'}</td>
                    <td>
                      Appointment #{row.appointmentId}
                      <br />
                      {formatTimestampIst(row.appointmentTimeIso)}
                    </td>
                    <td>
                      {row.acceptedValuedSumInr !== null
                        ? formatMinorUnitsInr(row.acceptedValuedSumInr * 100)
                        : ACCEPTED_AMOUNT_NOT_CONFIGURED_COPY}
                      {row.acceptedValuedSumInr !== null &&
                      row.notConfiguredCount > 0 ? (
                        <span
                          className="dashboard-page__note"
                          data-testid={`billing-queue-notconfig-${row.appointmentId}`}
                        >
                          {' '}
                          + {row.notConfiguredCount} accepted{' '}
                          {row.notConfiguredCount === 1 ? 'item' : 'items'}{' '}
                          without a configured amount
                        </span>
                      ) : null}
                    </td>
                    <td>
                      {row.collectedMinorUnits > 0
                        ? formatMinorUnitsInr(row.collectedMinorUnits)
                        : '-'}
                    </td>
                    <td data-testid={`billing-queue-outstanding-${row.appointmentId}`}>
                      {row.finalizedBills && row.finalizedBills.billCount>0
                        ? row.finalizedBills.state==='known' && row.finalizedBills.dueMinorUnits!==null
                          ? `${formatMinorUnitsInr(row.finalizedBills.dueMinorUnits)} bill balance`
                          : row.finalizedBills.state==='allocation_unknown'?'Bill allocation unknown':'Bill balance unavailable'
                        : row.state === 'not_configured'
                        ? ACCEPTED_AMOUNT_NOT_CONFIGURED_COPY
                        : row.outstandingMinorUnits !== null
                          ? formatMinorUnitsInr(row.outstandingMinorUnits)
                          : '-'}
                    </td>
                    <td>{queueAgeLabel(row.ageDays)}</td>
                    <td>
                      {/* The NULL-AMOUNT law: a not-configured row gets a
                          plain collect door with NO amount -- never
                          "Collect Rs 0". */}
                      <AcButton
                        variant="primary"
                        size="sm"
                        onClick={() =>
                          onOpenDrawer !== undefined
                            ? onOpenDrawer(row)
                            : onCollect(row)
                        }
                        data-testid={`billing-queue-collect-${row.appointmentId}`}
                      >
                        {row.state === 'outstanding' &&
                        row.outstandingMinorUnits !== null
                          ? `Collect ${formatMinorUnitsInr(row.outstandingMinorUnits)}`
                          : 'Collect'}
                      </AcButton>
                      {/* The explicit deep-work fallback: the full page form
                          (search, history, voids, unlinked, split). */}
                      {onOpenDrawer !== undefined ? (
                        <AcButton
                          variant="ghost"
                          size="sm"
                          onClick={() => onCollect(row)}
                          data-testid={`billing-queue-fullform-${row.appointmentId}`}
                        >
                          {QUEUE_FULL_FORM_COPY}
                        </AcButton>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )
      ) : null}
    </section>
  );
}

// ---- The convert-moment card (MONEY-COMPLETION-V1, V3-P3) ------------------

export interface ConvertMomentCardProps {
  readonly row: BillingQueueRow;
}

/** The money-capture moment as FIELDS: the episode, what was accepted, what
 *  is collected, what remains -- the obligation context the operator records
 *  against. Display-only (the amount input below stays the ONE write path). */
export function ConvertMomentCard({ row }: ConvertMomentCardProps): JSX.Element {
  return (
    <section
      className="dashboard-page__note"
      aria-label="Convert to revenue"
      data-testid="convert-moment-card"
    >
      <h2>Collect for this visit</h2>
      <dl className="billing-receipt__fields">
        <div>
          <dt>Patient</dt>
          <dd>{row.patientLabel ?? 'Name not recorded'}</dd>
        </div>
        <div>
          <dt>Visit</dt>
          <dd>
            Appointment #{row.appointmentId} -{' '}
            {formatTimestampIst(row.appointmentTimeIso)}
          </dd>
        </div>
        <div>
          <dt>Accepted treatment</dt>
          <dd data-testid="convert-moment-accepted">
            {row.acceptedCount > 0
              ? row.acceptedValuedSumInr !== null
                ? `${formatMinorUnitsInr(row.acceptedValuedSumInr * 100)} (${row.acceptedCount} ${row.acceptedCount === 1 ? 'item' : 'items'})`
                : `${ACCEPTED_AMOUNT_NOT_CONFIGURED_COPY} (${row.acceptedCount} accepted)`
              : 'None recorded'}
            {row.acceptedValuedSumInr !== null && row.notConfiguredCount > 0
              ? ` + ${row.notConfiguredCount} without a configured amount`
              : ''}
          </dd>
        </div>
        <div>
          <dt>Collected so far</dt>
          <dd data-testid="convert-moment-collected">
            {row.collectedMinorUnits > 0
              ? formatMinorUnitsInr(row.collectedMinorUnits)
              : 'Nothing yet'}
          </dd>
        </div>
        <div>
          <dt>State</dt>
          <dd data-testid="convert-moment-state">
            {row.finalizedBills && row.finalizedBills.billCount>0
              ? row.finalizedBills.state==='known' && row.finalizedBills.dueMinorUnits!==null
                ? `${formatMinorUnitsInr(row.finalizedBills.dueMinorUnits)} bill balance`
                : row.finalizedBills.state==='allocation_unknown'?'Bill allocation unknown':'Bill balance unavailable'
              : row.state === 'outstanding' && row.outstandingMinorUnits !== null
              ? `${formatMinorUnitsInr(row.outstandingMinorUnits)} outstanding`
              : episodeStateLabel(row.state)}
          </dd>
        </div>
      </dl>
    </section>
  );
}

// ---- The post-payment receipt state (MONEY-COMPLETION-V1, Scope 3f) --------

export interface BillingReceipt {
  readonly payment: PatientPaymentRow;
  /** FALSE = the writer's idempotent replay (already recorded; no new row). */
  readonly created: boolean;
}

export interface BillingReceiptPanelProps {
  readonly compact?: boolean;
  readonly receipt: BillingReceipt;
  /**
   * Explicit new-entry intent -- never a silently re-armed form. OPTIONAL
   * since SMART-PAYMENT-DRAWER-V1: a drawer is bound to ONE episode, so
   * "Record another payment" has no meaning there and the control is OMITTED
   * (the drawer's Full Billing link is the lawful door to an additional
   * payment). Every page caller still passes it, so the page render is
   * byte-identical.
   */
  readonly onRecordAnother?: () => void;
  /**
   * The in-page anchor to the recent-payments list. Defaults to the page's
   * own '#billing-recent' heading, so the page render is byte-identical.
   * NULL omits the link -- required when the receipt renders somewhere that
   * heading does not exist (the drawer over /walkin), because an anchor to a
   * missing target is a DEAD DOOR (LES-016).
   */
  readonly recentPaymentsHref?: string | null;
  readonly recentPaymentsLabel?: string;
  /** The receptionist's day-board home; null hides the link (role-held or
   *  absent -- LES-016). */
  readonly dayBoardPath: string | null;
  /** Standard 3 (journey continuity): the linked patient record path, or null
   *  when the role cannot open it (flag-gated; role-held-or-absent, LES-016).
   *  Default null keeps the existing pure renders byte-identical. */
  readonly patientRecordPath?: string | null;
  /** The REAL viewer role from /me (V7-P4 handoff-line audience wiring);
   *  null/absent falls closed to the role-neutral handoff copy. */
  readonly viewerRole?: string | null;
  /** RECEPTION-OS-BUILD-V1 (Q-03 + Q-DP-PRINT=B): opens the receipt PRINT
   *  view (the registered ReceiptPrintView over THIS committed payment).
   *  Absent = no print door (hosts without a page surface omit it). */
  readonly onPrintReceipt?: () => void;
  readonly receiptActionLabel?: string;
  /** DEEPAK-FRONT-OFFICE-FEEDBACK-CORRECTIVE-V1 (item 2): the /me payload the
   *  clinic-links bar projects (org-config Google review + Instagram URLs).
   *  Default null renders NO link bar, so every existing pure render stays
   *  byte-identical and an unconfigured org sees nothing (LES-016). */
  readonly me?: DashboardMe | null;
}

/** The structured receipt that REPLACES the editable form after a
 *  successful write: fields, not sentences; the form never looks
 *  still-ready-to-submit after success. */
export function BillingReceiptPanel({
  receipt,
  compact = false,
  onRecordAnother,
  dayBoardPath,
  patientRecordPath = null,
  viewerRole = null,
  me = null,
  recentPaymentsHref = '#billing-recent',
  recentPaymentsLabel = 'View in recent payments',
  onPrintReceipt,
  receiptActionLabel = 'Print receipt',
}: BillingReceiptPanelProps): JSX.Element {
  const p = receipt.payment;
  return (
    <section
      className="dashboard-page__note"
      aria-label="Payment recorded"
      data-testid="billing-receipt"
      role="status"
    >
      <h2>
        {receipt.created
          ? 'Payment recorded'
          : 'Already recorded - no duplicate created'}
      </h2>
      {/* HANDOFF LINE (V7-P4, #285): the payment-recorded completion moment
          states the follow-up leg via the shared NextActionBar (label-only
          band idiom; a scheduling ROUTE does not exist yet -- workflow-
          handoff.ts followupHandoff -- so the line names the REAL pickup
          queues instead of fabricating a door). Visit-linked payments only:
          an account-level payment completes no visit. */}
      {p.appointmentId !== null ? (
        <NextActionBar
          audience={viewerRole}
          primary={{
            label: compact ? 'Check Follow-ups when due.' : PAYMENT_RECORDED_NEXT_ACTION_LABEL,
            source: 'payment-recorded',
          }}
          noActionLabel={PAYMENT_RECORDED_NEXT_ACTION_LABEL}
          testId="billing-receipt-handoff"
        />
      ) : null}
      <dl className="billing-receipt__fields">
        <div>
          <dt>Payment</dt>
          {/* The status derives from the WIRE row, never hardcoded: an
              idempotent replay can return an already-VOIDED payment
              (recorded, response lost, supervisor voided, retry) -- the
              desk must see the truth (adversarial review S5). */}
          <dd data-testid="billing-receipt-id">
            #{p.id} - {p.voidedAt === null ? 'Recorded' : 'Voided'}
          </dd>
        </div>
        <div>
          <dt>Patient</dt>
          <dd>{p.patientName ?? p.patientId}</dd>
        </div>
        <div>
          <dt>UMR</dt>
          <dd>{p.receiptDetails?.patientUmr ?? 'Unavailable'}</dd>
        </div>
        <div>
          <dt>Clinic</dt>
          <dd>{p.receiptDetails?.clinicName ?? `Clinic #${p.clinicId}`}</dd>
        </div>
        <div>
          <dt>Consultant</dt>
          <dd>{p.receiptDetails?.sourceState !== 'available' ? 'Unavailable' : p.receiptDetails.consultantName ?? 'Not recorded'}</dd>
        </div>
        <div>
          <dt>Appointment date</dt>
          <dd>{p.receiptDetails?.sourceState !== 'available' ? 'Unavailable' : p.receiptDetails.appointmentAt
            ? formatTimestampIst(p.receiptDetails.appointmentAt) : 'Not recorded'}</dd>
        </div>
        <div>
          <dt>Visit</dt>
          <dd data-testid="billing-receipt-appointment">
            {p.appointmentId !== null
              ? `Appointment #${p.appointmentId}`
              : 'No visit link'}
          </dd>
        </div>
        <div>
          <dt>Amount</dt>
          <dd data-testid="billing-receipt-amount">
            {formatMinorUnitsInr(p.amountMinorUnits)}{p.currency === 'INR' ? '' : ` (${p.currency})`}
          </dd>
        </div>
        <div>
          <dt>Method</dt>
          <dd>{p.method ? paymentModeDescription(p) : '-'}</dd>
        </div>
        {/* L-03: the reference the operator just typed, echoed back on the
            receipt that replaced the form -- the SAME field and the SAME
            not-recorded honesty ReceiptPrintView prints. Rendered for BOTH
            hosts of this panel (the page and the collect drawer). */}
        <div>
          <dt>Reference</dt>
          <dd data-testid="billing-receipt-reference">
            {paymentReferenceDisplay(p)}
          </dd>
        </div>
        {/* COLLECT-PAYMENT-V1: the receipt's own time, addressable. It is
            the payment's OCCURRED-AT (when the money changed hands), never
            the row's created-at (when the record was written) -- the two
            differ for a backdated payment, which is exactly when a receipt
            that quietly showed the wrong one would matter. */}
        <div>
          <dt>Received at</dt>
          <dd data-testid="billing-receipt-received">
            {formatTimestampIst(p.occurredAt)}
          </dd>
        </div>
      </dl>
      <div className="billing-receipt__actions">
        {onPrintReceipt !== undefined ? (
          /* Q-03/Q-DP-PRINT=B: the desk prints receipts. The print view
             renders the COMMITTED row -- a print can never repeat the
             transaction (R-G / FS-29). */
          <AcButton
            variant="secondary"
            size="sm"
            onClick={onPrintReceipt}
            data-testid="billing-receipt-print"
          >
            {receiptActionLabel}
          </AcButton>
        ) : null}
        {recentPaymentsHref !== null ? (
          <a className={acButtonClass('secondary', 'sm')} href={recentPaymentsHref}>
            {recentPaymentsLabel}
          </a>
        ) : null}
        {dayBoardPath !== null ? (
          <Link
            className={acButtonClass('secondary', 'sm')}
            to={dayBoardPath}
            data-testid="billing-receipt-dayboard"
          >
            Return to the day board
          </Link>
        ) : null}
        {/* Standard 3 (journey continuity): payment -> patient record, one
            click away (payment -> appointment already exists via the receipt
            visit line). Disabled-by-absence when the role cannot open it. */}
        {patientRecordPath !== null ? (
          <Link
            className={acButtonClass('secondary', 'sm')}
            to={patientRecordPath}
            data-testid="billing-receipt-patient"
          >
            Open patient record
          </Link>
        ) : null}
        {onRecordAnother !== undefined ? (
          <AcButton
            variant="primary"
            size="sm"
            onClick={onRecordAnother}
            data-testid="billing-receipt-record-another"
          >
            Record another payment
          </AcButton>
        ) : null}
      </div>
      {/* DEEPAK-FRONT-OFFICE-FEEDBACK-CORRECTIVE-V1 (item 2; tenant feedback
          2026-07-27): the review / social ask at the VISIT-CLOSE moment -- the
          patient has just paid and is still at the desk, which is when the
          front desk actually makes it. Placed INSIDE the completion receipt
          (the existing checkout completion surface) rather than minting a new
          screen (REUSE-BEFORE-BUILD LAW). Renders NOTHING until the founder
          seeds the org's links, so an unconfigured org sees no change. */}
      <ClinicLinksBar me={me} />
    </section>
  );
}

/**
 * TREATMENT-ESTIMATE-ACCEPTANCE-AND-PATIENT-ACCOUNT-SPINE-V1: the ONE
 * receptionist checkout summary for the linked episode. FIELDS, NOT
 * SENTENCES; EXACTLY ONE primary action (server-derived token -> an
 * existing lawful door or an honest chip); payment honesty (accountBalance
 * ALWAYS 'not_available'; unknown prices render "Price unavailable");
 * discount evidence with original AND final values; the in-place discount
 * form renders ONLY when the server says the caller holds the authority
 * (render-only -- the backend gate is the boundary).
 */
export function CheckoutHandoffPanel({
  handoff,
  onChanged,
  patientRecordPath = null,
  variant = 'handoff',
  viewerRole,
  billActions,
  historyHref,
  detailsOpen=false,
  detailScopeKey,
}: {
  handoff: CheckoutHandoffResponse;
  /** Re-fetch signal after a discount apply / void. */
  onChanged: () => void;
  /** Standard 3 (journey continuity): the linked patient record path, or null
   *  when the role cannot open it. Default null keeps existing renders green. */
  patientRecordPath?: string | null;
  /** COLLECT-PAYMENT-V1. 'handoff' is the shipped panel, unchanged and the
   *  default. 'visit-details' is the SAME PANEL with the same facts, minus
   *  the three lines the collection composition already states above it (the
   *  patient, the primary action and its reason). A VARIANT, deliberately,
   *  not a second panel: one home for these facts, and no chance of the two
   *  drifting apart. Nothing is removed from the operator -- only from the
   *  screen that already said it. */
  variant?: 'handoff' | 'visit-details';
  viewerRole?: string | null;
  billActions?: React.ReactNode;
  historyHref?: string;
  detailsOpen?: boolean;
  detailScopeKey?: string;
}): JSX.Element {
  const [form, setForm] = useState<DiscountFormState>(EMPTY_DISCOUNT_FORM);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const actionHref = checkoutPrimaryActionHref(handoff);
  const actionLabel =
    CHECKOUT_PRIMARY_ACTION_LABELS[handoff.primaryAction.action];
  const reasonCopy =
    CHECKOUT_ACTION_REASON_COPY[handoff.primaryAction.reason] ??
    handoff.primaryAction.reason;
  const inr = (v: number): string => formatMinorUnitsInr(v * 100);
  // #311 / FND-37: a course beside the episode puts TWO grains on one
  // screen, so every figure states its own scope. No course -> one grain,
  // and the labels stay exactly as they are today.
  const coursePresent =
    handoff.courseContext !== null && handoff.courseContext !== undefined;
  const factLabel = checkoutFactLabels(coursePresent);
  const planItems =
    handoff.plan !== null && Array.isArray(handoff.plan.planItems)
      ? handoff.plan.planItems
      : null;
  const acceptedForDiscount = handoff.estimate.acceptedValueInr;
  const validation = validateDiscountForm(form, acceptedForDiscount);
  const showDiscountForm =
    handoff.canApplyDiscount &&
    handoff.discount.state === 'none' &&
    acceptedForDiscount !== null &&
    acceptedForDiscount > 0 &&
    handoff.plan !== null;

  const applyDiscount = async (): Promise<void> => {
    if (!validation.ok || handoff.plan === null) return;
    setBusy(true);
    setNote(null);
    try {
      await recordCaseSheetDiscount(handoff.plan.id, {
        discountKind: form.kind,
        ...(validation.percent !== null
          ? { discountPercent: validation.percent }
          : {}),
        ...(validation.amountInr !== null
          ? { discountAmountInr: validation.amountInr }
          : {}),
        reasonCode: form.reasonCode,
        requestId: crypto.randomUUID(),
      });
      setForm(EMPTY_DISCOUNT_FORM);
      onChanged();
    } catch (err) {
      setNote(err instanceof Error ? err.message : 'Discount failed');
    } finally {
      setBusy(false);
    }
  };

  const voidDiscount = async (): Promise<void> => {
    if (handoff.discount.id === null) return;
    setBusy(true);
    setNote(null);
    try {
      await voidTreatmentDiscountApi(handoff.discount.id, 'entry_error');
      onChanged();
    } catch (err) {
      setNote(err instanceof Error ? err.message : 'Void failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      /* COLLECT-PAYMENT-V1 -- THE RAIL IS A CARD NOW. `ac-card` and
         `dashboard-page__note` are styled by NO stylesheet in this repo
         (verified: zero matches across every .css file), which is why this
         panel rendered as a flat run of facts on the ground plane while
         everything around it sat on cards. On the composition it takes
         `ac-section` -- the SAME shipped card class BillWorkPanel in this
         file already uses -- so the rail reads as a surface. The clinic-wide
         desk keeps the markup it had. */
      className={variant === 'visit-details' ? 'ac-section' : 'ac-card dashboard-page__note'}
      aria-label={variant === 'visit-details' ? 'Visit details' : 'Checkout handoff'}
      data-testid="checkout-handoff"
    >
      {variant === 'visit-details'
        ? null
        : <p className="ac-section__eyebrow">Checkout handoff</p>}
      {/* Standard 3 (journey continuity): appointment/checkout -> patient
          record, one click away. Disabled-by-absence when the role cannot
          open it. */}
      {patientRecordPath !== null && variant !== 'visit-details' ? (
        <p data-testid="checkout-handoff-patient-link">
          <Link to={patientRecordPath}>
            {handoff.patientLabel ?? 'Open patient record'}
          </Link>
        </p>
      ) : null}
      {variant === 'visit-details' ? null : (
      <p data-testid="checkout-handoff-primary">
        <strong>{actionLabel}</strong>
        {actionHref !== null ? (
          <>
            {' '}
            <Link
              to={actionHref}
              className={acButtonClass('secondary', 'sm')}
              data-testid="checkout-handoff-primary-door"
            >
              Open
            </Link>
          </>
        ) : null}
      </p>
      )}
      {variant === 'visit-details' ? null : (
        <p data-testid="checkout-handoff-reason">{reasonCopy}</p>
      )}

      {variant === 'visit-details' ? <>
      <PaymentVisitDetails handoff={handoff} role={viewerRole} active={detailsOpen} scopeKey={detailScopeKey} historyHref={historyHref} showBills={false} billActions={billActions}/>
      {handoff.discount.state !== 'none' ? <div className="payment-detail-discount"><h3>Discount approval</h3>
            {handoff.discount.state === 'applied' &&
            handoff.discount.originalAcceptedValueInr !== null &&
            handoff.discount.discountAmountInr !== null &&
            handoff.discount.finalAcceptedValueInr !== null ? (
              <>
                {inr(handoff.discount.originalAcceptedValueInr)} -{' '}
                {inr(handoff.discount.discountAmountInr)}
                {handoff.discount.discountKind === 'percent' &&
                handoff.discount.discountPercent !== null
                  ? ` (${handoff.discount.discountPercent}%)`
                  : ''}{' '}
                = <strong>{inr(handoff.discount.finalAcceptedValueInr)}</strong>
                {' - '}
                {discountReasonLabel(handoff.discount.reasonCode ?? '')}
                {handoff.discount.approvedByRole !== null
                  ? `, approved by ${discountApproverRoleLabel(handoff.discount.approvedByRole)}`
                  : ''}
                {handoff.estimate.estimateChangedSinceDiscount
                  ? ' (the accepted estimate changed after approval - review)'
                  : ''}
                {handoff.canApplyDiscount && handoff.discount.id !== null ? (
                  <>
                    {' '}
                    <AcButton
                      variant="secondary"
                      size="sm"
                      disabled={busy}
                      onClick={() => void voidDiscount()}
                      data-testid="checkout-handoff-discount-void"
                    >
                      Void discount
                    </AcButton>
                  </>
                ) : null}
              </>
            ) : handoff.discount.state === 'unavailable' ? (
              'Discount state unavailable'
            ) : (
              'No discount applied'
            )}
          </div> : null}
      </> : <>
      <dl className="convert-moment__facts" data-testid="checkout-handoff-facts">
        <div>
          <dt>{factLabel.conversion}</dt>
          <dd data-testid="checkout-handoff-conversion">
            {COMMERCIAL_CONVERSION_LABELS[handoff.commercialConversionState]}
          </dd>
        </div>
        <div>
          <dt>{factLabel.accepted}</dt>
          <dd data-testid="checkout-handoff-accepted">
            {handoff.decisionCounts.acceptedCount}
            {handoff.estimate.acceptedValueInr !== null
              ? ` - ${inr(handoff.estimate.acceptedValueInr)}`
              : handoff.decisionCounts.acceptedCount > 0
                ? ` - ${PRICE_UNAVAILABLE_COPY}`
                : ''}
            {handoff.estimate.unknownPriceAcceptedCount > 0
              ? ` (${handoff.estimate.unknownPriceAcceptedCount} without a price)`
              : ''}
          </dd>
        </div>
        <div>
          <dt>{factLabel.decisions}</dt>
          <dd data-testid="checkout-handoff-decisions">
            {handoff.decisionCounts.deferredCount} deferred
            {handoff.estimate.deferredValueInr !== null
              ? ` (${inr(handoff.estimate.deferredValueInr)})`
              : ''}
            {' / '}
            {handoff.decisionCounts.declinedCount} declined
            {' / '}
            {handoff.decisionCounts.undecidedCount} undecided
          </dd>
        </div>
        <div>
          <dt>{factLabel.recommended}</dt>
          <dd data-testid="checkout-handoff-recommended">
            {handoff.estimate.recommendedValueInr !== null
              ? inr(handoff.estimate.recommendedValueInr)
              : handoff.decisionCounts.itemCount > 0
                ? PRICE_UNAVAILABLE_COPY
                : 'No items recommended'}
            {handoff.estimate.unknownPriceItemCount > 0
              ? ` (${handoff.estimate.unknownPriceItemCount} item${handoff.estimate.unknownPriceItemCount === 1 ? '' : 's'} without a price)`
              : ''}
          </dd>
        </div>
        <div>
          <dt>{factLabel.discount}</dt>
          <dd data-testid="checkout-handoff-discount">
            {handoff.discount.state === 'applied' &&
            handoff.discount.originalAcceptedValueInr !== null &&
            handoff.discount.discountAmountInr !== null &&
            handoff.discount.finalAcceptedValueInr !== null ? (
              <>
                {inr(handoff.discount.originalAcceptedValueInr)} -{' '}
                {inr(handoff.discount.discountAmountInr)}
                {handoff.discount.discountKind === 'percent' &&
                handoff.discount.discountPercent !== null
                  ? ` (${handoff.discount.discountPercent}%)`
                  : ''}{' '}
                = <strong>{inr(handoff.discount.finalAcceptedValueInr)}</strong>
                {' - '}
                {discountReasonLabel(handoff.discount.reasonCode ?? '')}
                {handoff.discount.approvedByRole !== null
                  ? `, approved by ${discountApproverRoleLabel(handoff.discount.approvedByRole)}`
                  : ''}
                {handoff.estimate.estimateChangedSinceDiscount
                  ? ' (the accepted estimate changed after approval - review)'
                  : ''}
                {handoff.canApplyDiscount && handoff.discount.id !== null ? (
                  <>
                    {' '}
                    <AcButton
                      variant="secondary"
                      size="sm"
                      disabled={busy}
                      onClick={() => void voidDiscount()}
                      data-testid="checkout-handoff-discount-void"
                    >
                      Void discount
                    </AcButton>
                  </>
                ) : null}
              </>
            ) : handoff.discount.state === 'unavailable' ? (
              'Discount state unavailable'
            ) : (
              'No discount applied'
            )}
          </dd>
        </div>
        <div>
          <dt>{factLabel.payment}</dt>
          <dd data-testid="checkout-handoff-payment">
            {PAYMENT_EVIDENCE_LABELS[handoff.paymentEvidence]}
            {handoff.collectedMinorUnits > 0
              ? ` - ${formatMinorUnitsInr(handoff.collectedMinorUnits)} recorded on this visit`
              : ''}
            {handoff.unlinkedPaymentCount !== null &&
            handoff.unlinkedPaymentCount > 0
              ? ` - ${handoff.unlinkedPaymentCount} payment${handoff.unlinkedPaymentCount === 1 ? '' : 's'} recorded without appointment linkage`
              : ''}
          </dd>
        </div>
        {handoff.finalizedBills && handoff.finalizedBills.billCount>0 ? <div>
          <dt>Finalized bill balance</dt>
          <dd data-testid="checkout-handoff-bill-balance">{handoff.finalizedBills.state==='known' && handoff.finalizedBills.dueMinorUnits!==null
            ? formatMinorUnitsInr(handoff.finalizedBills.dueMinorUnits)
            : handoff.finalizedBills.state==='allocation_unknown'?'Bill allocation unknown':'Bill balance unavailable'}</dd>
        </div> : null}
        {handoff.followUpRecommendation !== null &&
        handoff.followUpRecommendation !== undefined ? (
          // DOCTOR-CLINICAL-ENCOUNTER-FOUNDATION-V1: the doctor's FINALIZED
          // follow-up recommendation -- the booking instruction the front
          // desk acts on (operational handoff content; never the clinical
          // body). A token that left the org vocabulary renders raw
          // (never invented).
          <div>
            <dt>Doctor follow-up</dt>
            <dd data-testid="checkout-handoff-followup-recommendation">
              {handoff.followUpRecommendation.optionLabel ??
                handoff.followUpRecommendation.optionKey}
              {handoff.followUpRecommendation.returnRequest ? <span>
                {` · Requested doctor: ${handoff.followUpRecommendation.returnRequest.requestedDoctorName ?? (handoff.followUpRecommendation.returnRequest.requestedDoctorId === null ? 'Not set' : 'Name unavailable')}`}
                {handoff.followUpRecommendation.returnRequest.durationMinutes !== null ? ` · ${handoff.followUpRecommendation.returnRequest.durationMinutes} min requested` : ' · Time needed not set'}
              </span> : null}
              {handoff.followUpRecommendation.note !== null &&
              handoff.followUpRecommendation.note !== ''
                ? ` - ${handoff.followUpRecommendation.note}`
                : ''}
            </dd>
          </div>
        ) : null}
        {handoff.courseContext !== null &&
        handoff.courseContext !== undefined ? (
          <div>
            <dt>{factLabel.course}</dt>
            <dd data-testid="checkout-handoff-course">
              {handoff.courseContext.state === 'unavailable' ? (
                COURSE_CONTEXT_UNAVAILABLE_COPY
              ) : (
                <>
                  {handoff.courseContext.courseName ??
                    `Journey #${handoff.courseContext.courseId ?? ''}`}
                  {handoff.courseContext.visitCount !== null
                    ? ` - ${handoff.courseContext.visitCount} visit${handoff.courseContext.visitCount === 1 ? '' : 's'}`
                    : ''}
                  {handoff.courseContext.progress !== null
                    ? handoff.courseContext.completionAvailability ===
                      'available'
                      ? ` - ${handoff.courseContext.progress.completedCount}/${handoff.courseContext.progress.acceptedCount} accepted items completed`
                      : ` - ${COURSE_COMPLETION_UNAVAILABLE_COPY}`
                    : ''}
                  {handoff.courseContext.ledger !== null ? (
                    <>
                      <br />
                      {COURSE_LEDGER_STATE_LABELS[
                        handoff.courseContext.ledger.state
                      ] ?? handoff.courseContext.ledger.state}
                      {handoff.courseContext.ledger
                        .closedAcceptedValuedSumInr !== null
                        ? ` - ${COURSE_TOTAL_COPY} ${inr(handoff.courseContext.ledger.closedAcceptedValuedSumInr)}`
                        : ''}
                      {handoff.courseContext.ledger.closedNotConfiguredCount > 0
                        ? ` (${handoff.courseContext.ledger.closedNotConfiguredCount} accepted item${handoff.courseContext.ledger.closedNotConfiguredCount === 1 ? '' : 's'} without a price)`
                        : ''}
                      {handoff.courseContext.ledger.collectedMinorUnits > 0
                        ? ` - ${COURSE_COLLECTED_COPY} ${formatMinorUnitsInr(handoff.courseContext.ledger.collectedMinorUnits)}`
                        : ''}
                      {handoff.courseContext.ledger.outstandingMinorUnits !==
                      null ? (
                        <>
                          {` - ${COURSE_REMAINING_COPY} `}
                          <strong data-testid="checkout-handoff-course-remaining">
                            {formatMinorUnitsInr(
                              handoff.courseContext.ledger
                                .outstandingMinorUnits,
                            )}
                          </strong>
                        </>
                      ) : null}
                    </>
                  ) : null}
                </>
              )}
            </dd>
          </div>
        ) : null}
        <div>
          <dt>Proposed payment structure</dt>
          <dd data-testid="checkout-handoff-structure">
            {PROPOSED_PAYMENT_STRUCTURE_LABELS[handoff.proposedPaymentStructure]}
          </dd>
        </div>
        <div>
          <dt>Account</dt>
          <dd data-testid="checkout-handoff-balance">{CHECKOUT_BALANCE_COPY}</dd>
        </div>
        <div>
          <dt>Next appointment</dt>
          <dd data-testid="checkout-handoff-next-appointment">
            {NEXT_APPOINTMENT_LABELS[handoff.nextAppointment]}
          </dd>
        </div>
        <div>
          <dt>Follow-up</dt>
          <dd data-testid="checkout-handoff-followup">
            {FOLLOW_UP_LABELS[handoff.followUp]}
          </dd>
        </div>
      </dl>

      {planItems !== null && planItems.length > 0 ? (
        <ul data-testid="checkout-handoff-items">
          {planItems.map((i) => (
            <li key={i.id}>
              {i.procedureName} - {i.status}
              {' - '}
              {i.estimatedValue !== null
                ? inr(i.estimatedValue)
                : PRICE_UNAVAILABLE_COPY}
            </li>
          ))}
        </ul>
      ) : null}

      {/* The doctor/reception COMMERCIAL HANDOFF boundary (corrective
          section E), stated in-product: the panel carries NO decision or
          estimate editor -- reception records collections and scheduling
          through the existing doors only. */}
      <p data-testid="checkout-handoff-boundary">{CHECKOUT_BOUNDARY_COPY}</p>

      </>}
      {showDiscountForm ? (
        <div data-testid="checkout-handoff-discount-form">
          <p className="ac-section__eyebrow">Apply discount (authorized)</p>
          <label>
            Kind{' '}
            <select
              value={form.kind}
              disabled={busy}
              data-testid="checkout-discount-kind"
              onChange={(e) =>
                setForm({
                  ...form,
                  kind: e.target.value === 'fixed' ? 'fixed' : 'percent',
                })
              }
            >
              <option value="percent">Percent</option>
              <option value="fixed">Fixed amount</option>
            </select>
          </label>{' '}
          {form.kind === 'percent' ? (
            <label>
              Percent{' '}
              <input
                type="text"
                inputMode="numeric"
                value={form.percentRaw}
                disabled={busy}
                data-testid="checkout-discount-percent"
                onChange={(e) => setForm({ ...form, percentRaw: e.target.value })}
              />
            </label>
          ) : (
            <label>
              Amount (whole ₹){' '}
              <input
                type="text"
                inputMode="numeric"
                value={form.amountRaw}
                disabled={busy}
                data-testid="checkout-discount-amount"
                onChange={(e) => setForm({ ...form, amountRaw: e.target.value })}
              />
            </label>
          )}{' '}
          <label>
            Reason{' '}
            <select
              value={form.reasonCode}
              disabled={busy}
              data-testid="checkout-discount-reason"
              onChange={(e) => setForm({ ...form, reasonCode: e.target.value })}
            >
              <option value="">Pick a reason (required)</option>
              {handoff.discountReasonCodes.map((code) => (
                <option key={code} value={code}>
                  {discountReasonLabel(code)}
                </option>
              ))}
            </select>
          </label>{' '}
          <AcButton
            variant="primary"
            size="sm"
            disabled={busy || !validation.ok}
            onClick={() => void applyDiscount()}
            data-testid="checkout-discount-apply"
          >
            Apply discount
          </AcButton>
          {validation.error !== null && !validation.ok ? (
            <p role="status" data-testid="checkout-discount-validation">
              {validation.error}
            </p>
          ) : null}
        </div>
      ) : null}
      {note !== null ? (
        <p role="status" data-testid="checkout-handoff-note">
          {note}
        </p>
      ) : null}
    </section>
  );
}

// ---- BILLING-MISSING-DELTA-V1: the itemized appointment bill panel --------

interface BillWorkPanelProps {
  readonly expectedPatientFamilyIds?: readonly string[];
  readonly selectedBillId?: number;
  readonly documentActions?: React.ReactNode;
  readonly clinicLabel?: string;
  readonly viewerRole?: string | null;
  readonly consultationScopeKey?: string;
  readonly consultationHints?: { readonly canWaive: boolean; readonly canReverseWaiver: boolean };
  readonly reloadBillingContext?: () => Promise<void>;
  readonly headerActionTarget?: HTMLElement | null;
  readonly appointmentId: number;
  readonly clinicId: number;
  /** Print door lifted to the page (the receipt-print swap idiom). */
  readonly onPrintBill: (bill: PatientBillWireMirror) => void;
}

type BillPanelState =
  | { readonly kind: 'loading' }
  /** 20260830_1 unapplied (the honest 409), OR a serving side that does not
   *  carry the bill wire at all (stale deploy / mock default / malformed
   *  answer -- the walk24-2 corrective): the SAME visibly honest line,
   *  never a dead Start button, never a crash, never silently blank. */
  | { readonly kind: 'unavailable' }
  | { readonly kind: 'error'; readonly message: string }
  | {
      readonly kind: 'ready';
      readonly appointmentId: number;
      readonly scopeKey?: string;
      readonly consultationByBill?: Readonly<Record<string, ConsultationBillState & {readonly activity?: ConsultationActivityDisplay | null; readonly reversalActivity?: ConsultationActivityDisplay | null}>>;
      readonly bills: readonly PatientBillWireMirror[];
      readonly canCancelFinalized: boolean;
    };

/**
 * The visit's itemized-bill workspace: mint a draft (seeded from the
 * doctor's ACCEPTED plan as a snapshot -- the plan is never written), add /
 * edit / waive / delete lines while draft, record a GST amount, finalize
 * (freeze), print, and cancel (the void posture: supervisory once
 * finalized). Edits save together on explicit submit; every number displayed comes off
 * the wire's derived totals.
 */
export function BillWorkPanel(props: BillWorkPanelProps): JSX.Element {
  const identity = useConsultationIdentity();
  return <BillWorkPanelWorkspace key={JSON.stringify([identity, props.consultationScopeKey, props.clinicId, props.appointmentId])} {...props} />;
}

function BillWorkPanelWorkspace({
  selectedBillId, clinicLabel, documentActions, expectedPatientFamilyIds,
  viewerRole = null,
  consultationScopeKey, consultationHints, reloadBillingContext,
  headerActionTarget = null,
  appointmentId,
  clinicId,
  onPrintBill,
}: BillWorkPanelProps): JSX.Element {
  const mounted = useRef(false);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const [state, setState] = useState<BillPanelState>({ kind: 'loading' });
  const [refresh, setRefresh] = useState(0);
  const [pendingBillId, setPendingBillId] = useState<number | null>(null);
  const [billRecoveryError,setBillRecoveryError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled=false;
    pendingBillMutationId(appointmentId).then(id=>{if(!cancelled){setPendingBillId(id);setBillRecoveryError(null);}})
      .catch(()=>{if(!cancelled)setBillRecoveryError('Bill recovery is unavailable. Sign in again before changing this bill.');});
    return ()=>{cancelled=true;};
  },[refresh,consultationScopeKey,appointmentId]);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<Feedback | null>(null);
  const [consultationOperationLocked, setConsultationOperationLocked] = useState(false);
  useEffect(() => { setConsultationOperationLocked(false); }, [appointmentId, consultationScopeKey]);
  const consultationUnavailable = state.kind === 'ready' && state.bills.some(bill =>
    bill.status === 'draft' && state.consultationByBill?.[String(bill.id)]?.state === 'unavailable');
  const billWritesLocked = consultationOperationLocked || consultationUnavailable || pendingBillId!==null || billRecoveryError!==null;
  const billWritesLockedRef = useRef(billWritesLocked);
  billWritesLockedRef.current = billWritesLocked;
  const [doctorOptions, setDoctorOptions] = useState<
    readonly BillDoctorOption[] | null
  >(null);
  // The mint's single-use idempotency key (the payments posture): re-armed
  // per appointment; a retry after failure keeps the same key.
  const [mintRequestId, setMintRequestId] = useState(() => crypto.randomUUID());
  const [cancellingBillId, setCancellingBillId] = useState<number | null>(null);
  const [draftCancelId, setDraftCancelId] = useState<number | null>(null);
  const [draftCancelReason, setDraftCancelReason] = useState('');
  const draftCancelTrigger = useRef<HTMLButtonElement>(null);
  const draftCancelSelect = useRef<HTMLSelectElement>(null);
  useEffect(() => { setDraftCancelId(null); setDraftCancelReason(''); }, [appointmentId, clinicId, consultationScopeKey]);
  useEffect(() => { if (draftCancelId !== null) draftCancelSelect.current?.focus(); }, [draftCancelId]);
  const dismissDraftCancel = () => {
    setDraftCancelId(null); setDraftCancelReason('');
    draftCancelTrigger.current?.focus();
  };
  const [draftEditorVersion,setDraftEditorVersion]=useState(0);
  useEffect(() => { setMintRequestId(crypto.randomUUID()); }, [appointmentId]);

  const billReadGeneration = useRef(0);
  const loadBills = useCallback(async () => {
    if (!mounted.current) return;
    const generation = ++billReadGeneration.current;
    const res = await fetchPatientBills({ appointmentId, ...(expectedPatientFamilyIds ? {clinicId} : {}) });
    if (!mounted.current || generation !== billReadGeneration.current) return;
    if (expectedPatientFamilyIds && !billListMatchesScope(res, {patientId: null, clinicId, appointmentId, billId: null, patientFamilyIds: expectedPatientFamilyIds})) {
      setState({kind: 'unavailable'});
      throw new PortalApiError(409, 'Bill scope could not be confirmed', 'bill_store_not_available');
    }
    const read = readPatientBillListWire(res);
    if (read.kind === 'not_served') throw new PortalApiError(409, 'Bill details are not available', 'bill_store_not_available');
    setState({ kind: 'ready', appointmentId, scopeKey: consultationScopeKey, bills: read.bills,
      canCancelFinalized: read.canCancelFinalized, consultationByBill: res.consultationByBill });
  }, [appointmentId, consultationScopeKey, clinicId, expectedPatientFamilyIds]);
  useEffect(() => {
    let cancelled = false;
    setState(previous => previous.kind === 'ready' && previous.appointmentId === appointmentId && previous.scopeKey === consultationScopeKey ? previous : { kind: 'loading' });
    void loadBills().catch((err: unknown) => {
      if (cancelled) return;
      setState(previous => previous.kind === 'ready' && previous.appointmentId === appointmentId && previous.scopeKey === consultationScopeKey ? previous :
        isBillStoreUnavailableError(err) ? { kind: 'unavailable' } : { kind: 'error', message: safeErrorMessage(err, 'Failed to load bills') });
      setNote({ kind: 'error', text: 'Could not refresh bill details. Last confirmed rows remain visible.' });
    });
    return () => { cancelled = true; billReadGeneration.current++; };
  }, [appointmentId, refresh, loadBills]);
  const reloadConsultation = async () => {
    await reloadBillingContext?.();
    await loadBills();
    if (mounted.current) setDraftEditorVersion(n => n + 1);
  };

  // The doctor roster for the line-doctor pick (lazy; honest absence on
  // fault OR a malformed wire -- lines stay recordable without a doctor).
  useEffect(() => {
    let cancelled = false;
    fetchBillDoctorOptions(clinicId)
      .then((res) => {
        if (!cancelled) setDoctorOptions(readBillDoctorOptionsWire(res));
      })
      .catch(() => {
        if (!cancelled) setDoctorOptions(null);
      });
    return () => {
      cancelled = true;
    };
  }, [clinicId]);

  const run = useCallback(
    (act: () => Promise<unknown>, successText: string | null) => {
      if (!mounted.current) return;
      setBusy(true);
      setNote(null);
      act()
        .then(() => {
          if (!mounted.current) return;
          setBusy(false);
          if (successText !== null) {
            setNote({ kind: 'success', text: successText });
          }
          setRefresh((n) => n + 1);
        })
        .catch((err: unknown) => {
          if (!mounted.current) return;
          setBusy(false);
          if (isBillStoreUnavailableError(err)) {
            setState({ kind: 'unavailable' });
            return;
          }
          setNote({
            kind: 'error',
            text: safeErrorMessage(err, 'Bill update failed'),
          });
          setRefresh((n) => n + 1);
        });
    },
    [],
  );

  const runDraft = useCallback((act: () => Promise<unknown>, successText: string | null) => {
    if (billWritesLockedRef.current) return;
    run(act, successText);
  }, [run]);

  if (state.kind === 'loading' || (state.kind === 'ready' && (state.appointmentId !== appointmentId || state.scopeKey !== consultationScopeKey))) {
    return (
      <section className="ac-section" data-testid="bill-panel-loading">
        <h2>Itemised bill (this visit)</h2>
        <p className="dashboard-page__note">Loading bills...</p>
      </section>
    );
  }
  if (state.kind === 'unavailable') {
    // PRE-APPLY / NOT-SERVED HONESTY: the store's migration is founder-owned
    // (or this server does not carry the bill wire yet); no fake button, no
    // silent hide, no crash -- the seam is named in-product (LES-016).
    return (
      <section className="ac-section" data-testid="bill-panel-unavailable">
        <h2>Itemised bill (this visit)</h2>
        <p className="dashboard-page__note">
          Itemised bills are not available on this server yet (the bill
          store&apos;s database change awaits the founder apply). Payments
          above work exactly as before.
        </p>
      </section>
    );
  }
  if (state.kind === 'error') {
    return (
      <section className="ac-section" data-testid="bill-panel-error">
        <h2>Itemised bill (this visit)</h2>
        <ErrorState
          title="Bills could not load right now."
          detail={state.message}
          audience={null}
          testId="bill-panel-error-state"
        />
      </section>
    );
  }

  const visibleBills = selectedBillId === undefined ? state.bills : state.bills.filter(b => b.id === selectedBillId);
  const draft = visibleBills.find((b) => b.status === 'draft') ?? null;
  const nonDraft = visibleBills.filter((b) => b.status !== 'draft');
  const consultation = draft ? state.consultationByBill?.[String(draft.id)] : undefined;
  const pendingPlanSnapshot = draft?.consultationPlanSnapshotPending === true;
  const snapshotUnknown = draft !== null && draft.consultationPlanSnapshotPending !== true && draft.consultationPlanSnapshotPending !== false;
  const startBillButton = (testId = 'bill-start') => (
          <AcButton
            variant="secondary"
            disabled={busy}
            onClick={() =>
              runDraft(
                () =>
                  createPatientBillApi({
                    clinicId,
                    appointmentId,
                    requestId: mintRequestId,
                  }).then(async () => {
                    // Adoption keeps the bill id. Read its accepted-plan snapshot
                    // before remounting the existing editor's saved baseline.
                    await loadBills();
                    if (mounted.current) {
                      setMintRequestId(crypto.randomUUID());
                      setDraftEditorVersion(n => n + 1);
                    }
                  }),
                'Draft bill started.',
              )
            }
            data-testid={testId}
          >
            {pendingPlanSnapshot || nonDraft.length === 0
              ? 'Start itemised bill'
              : 'Start a new bill (correction)'}
          </AcButton>
  );

  return (
    <section className={state.bills.length === 0 ? "ac-section ac-collect__bill-empty" : "ac-section"} data-testid="bill-panel">
      {selectedBillId === undefined && (draft === null || pendingPlanSnapshot) && headerActionTarget !== null
        ? createPortal(startBillButton('collect-header-bill-start'), headerActionTarget)
        : null}
      <h2 className="ac-section__title">Itemised bill (this visit)</h2>

      <FeedbackBanner feedback={note} />
      {billRecoveryError ? <p role="alert">{billRecoveryError}</p> : null}
      {pendingBillId !== null ? <div role="status" data-testid="bill-change-recovery">
        <p>A bill change is unconfirmed. Check it before making another change.</p>
        {(pendingBillId===0 || state.bills.some(b => b.id === pendingBillId)) ? <AcButton disabled={busy} onClick={() =>
          run(async () => {
            await retryPendingBillMutation(state.bills.map(b => b.id), appointmentId);
            await loadBills();
            if (mounted.current) setDraftEditorVersion(n => n + 1);
          }, 'Saved bill checked.')
        }>Check saved bill change</AcButton> : <p>Open {pendingBillId<0 ? `visit #${-pendingBillId}` : `bill #${pendingBillId}`} to resolve its saved change.</p>}
      </div> : null}

      {draft === null ? (
        selectedBillId === undefined ? <div className="billing-entry__field">
          {/* Mint door: seeds a DRAFT from the visit's accepted plan
              (snapshot copy; unvalued items arrive as "Price not set"). */}
          {startBillButton()}
        </div> : visibleBills.length === 0 ? <p role="alert">Bill unavailable for this visit.</p> : null
      ) : (
        <div data-testid="bill-editor">
          <BillDetailCard bill={draft} testId="bill-draft-detail" documentLayout clinicLabel={clinicLabel} />
          {pendingPlanSnapshot ? <div className="billing-entry__field">{startBillButton()}</div> : null}
          {snapshotUnknown ? <ErrorState title="Bill preparation unavailable" detail="The accepted-treatment snapshot could not be confirmed."
            action={<AcButton onClick={() => void loadBills().catch(() => setNote({kind:'error',text:'Could not refresh bill details.'}))}>Check saved state</AcButton>} /> : null}
          <div className="bill-document-actions"><AcButton variant="secondary" data-testid={`bill-print-${draft.id}`} onClick={() => onPrintBill(draft)}>Print / Save PDF</AcButton>{documentActions}</div>
          {consultation?.state === 'unavailable' ? <ErrorState title="Fee unavailable" detail="Check the saved fee." action={<AcButton onClick={() => void reloadConsultation().catch(() => setNote({ kind: 'error', text: 'Could not refresh consultation details.' }))}>Check saved state</AcButton>} /> : null}
          {consultation?.state === 'ready' && consultationScopeKey ? <section aria-label="Consultation fee" data-testid="billing-consultation-fee">
            <h3 className="ac-section__title">Consultation fee</h3>
            <p><AcBadge tone={consultation.feeRecorded?'positive':'pending'}>{consultation.reversed?'Last confirmed · Reversed':consultation.waived?'Last confirmed · Waived':consultation.feeRecorded?'Last confirmed':'Not recorded'}</AcBadge></p>
            {consultation.activity?<ConsultationAttribution label="Waived" activity={consultation.activity}/>:null}
            {consultation.reversalActivity?<ConsultationAttribution label="Reversed" activity={consultation.reversalActivity}/>:null}
            {!consultation.feeRecorded&&!consultation.canRecordFee?<p>Fee cannot be determined yet.</p>:null}
            <ConsultationActions contextKey={`${consultationScopeKey}:${appointmentId}:${draft.id}`} appointmentId={appointmentId}
              bill={{ id: draft.id, status: draft.status, feeRecorded: consultation.feeRecorded, waived: consultation.waived, reversed: consultation.reversed, originalActor: consultation.originalActor }}
              course={null} hints={{ canRecordFee: consultation.canRecordFee, canWaive: consultation.canWaive && consultationHints?.canWaive === true, canReverseWaiver: consultationHints?.canReverseWaiver === true, canCompleteCourse: false, canAbandonCourse: false }}
              reload={reloadConsultation} onLockChange={setConsultationOperationLocked} />
          </section> : null}
          {billWritesLocked?<p role="status">Check consultation state before changing or finalizing this bill.</p>:null}
          <BillDraftEditor key={`${draft.id}:${draftEditorVersion}`} bill={draft} doctors={doctorOptions} canWaive={viewerRole!==null&&viewerRole!=='receptionist'}
            locked={busy||billWritesLocked||pendingPlanSnapshot||snapshotUnknown} managedItemId={consultation?.state==='ready'?consultation.managedItemId:null}
            onSave={payload=>runDraft(async()=>{
              await savePatientBillDraftApi(draft.id,payload);
              await loadBills();
              if(mounted.current)setDraftEditorVersion(n=>n+1);
            },payload.finalize?'Bill finalized.':'Bill saved.')} />
          <div className="billing-entry__field">
            <AcButton
              ref={draftCancelTrigger}
              variant="ghost"
              disabled={busy}
              aria-expanded={draftCancelId === draft.id}
              onClick={() => { setDraftCancelId(draft.id); setDraftCancelReason(''); }}
              data-testid="bill-cancel-draft"
            >
              Cancel draft
            </AcButton>
            {draftCancelId === draft.id ? (
              <div role="group" aria-label="Cancel draft bill" data-testid="bill-cancel-draft-choice"
                onKeyDown={(event) => {
                  if (event.key === 'Escape') {
                    event.preventDefault(); event.stopPropagation();
                    if (!busy) dismissDraftCancel();
                  }
                }}>
                <label>
                  Cancel draft reason
                  <select ref={draftCancelSelect} value={draftCancelReason}
                    onChange={(event) => setDraftCancelReason(event.target.value)}
                    disabled={busy} data-testid="bill-cancel-draft-reason">
                    <option value="">Choose a reason</option>
                    {BILL_CANCEL_REASON_OPTIONS.map(reason => <option key={reason} value={reason}>{reason.replace(/_/g, ' ')}</option>)}
                  </select>
                </label>
                <AcButton variant="secondary" disabled={busy} data-testid="bill-cancel-draft-confirm"
                  onClick={() => {
                    const reason = BILL_CANCEL_REASON_OPTIONS.find(value => value === draftCancelReason);
                    if (!reason) {
                      setNote({ kind: 'error', text: 'Choose a cancellation reason.' });
                      draftCancelSelect.current?.focus();
                      return;
                    }
                    runDraft(() => cancelPatientBillApi(draft.id, reason, 'draft').then(() => {
                      setDraftCancelId(null); setDraftCancelReason('');
                    }), 'Draft bill cancelled.');
                  }}>Cancel draft</AcButton>
                <AcButton variant="ghost" disabled={busy} onClick={dismissDraftCancel}
                  data-testid="bill-cancel-draft-back">Keep bill</AcButton>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {nonDraft.length > 0 ? (
        <div data-testid="bill-history">
          {nonDraft.map((bill) => (
            <div key={bill.id} data-testid={`bill-card-${bill.id}`}>
              <BillDetailCard bill={bill} testId={`bill-detail-${bill.id}`} documentLayout clinicLabel={clinicLabel} />
              <div className="billing-entry__field">
                <AcButton
                  variant="secondary"
                  size="sm"
                  onClick={() => onPrintBill(bill)}
                  data-testid={`bill-print-${bill.id}`}
                >
                  {selectedBillId === undefined ? 'Print bill' : 'Print / Save PDF'}
                </AcButton>{documentActions}{' '}
                {bill.status === 'finalized' && state.canCancelFinalized ? (
                  cancellingBillId === bill.id ? (
                    <span data-testid={`bill-cancel-confirm-${bill.id}`}>
                      {BILL_CANCEL_REASON_OPTIONS.map((reason) => (
                        <AcButton
                          key={reason}
                          variant="secondary"
                          size="sm"
                          disabled={busy}
                          onClick={() => {
                            setCancellingBillId(null);
                            run(
                              () => cancelPatientBillApi(bill.id, reason),
                              'Bill cancelled. Start a new bill if a corrected one is needed.',
                            );
                          }}
                          data-testid={`bill-cancel-${bill.id}-${reason}`}
                        >
                          {reason.replace(/_/g, ' ')}
                        </AcButton>
                      ))}
                      <AcButton
                        variant="ghost"
                        size="sm"
                        onClick={() => setCancellingBillId(null)}
                      >
                        Keep bill
                      </AcButton>
                    </span>
                  ) : (
                    <AcButton
                      variant="ghost"
                      size="sm"
                      disabled={busy}
                      onClick={() => setCancellingBillId(bill.id)}
                      data-testid={`bill-cancel-${bill.id}`}
                    >
                      Cancel bill
                    </AcButton>
                  )
                ) : null}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}

/** THE ONE back-path for this page. The breadcrumb's "Today" crumb and the
 *  composition's Cancel resolve to the SAME constant, so a future change to
 *  where this screen returns cannot move one and leave the other behind. */
const BACK_TO_TODAY_PATH = '/walkin';

type CollectionPayload = Parameters<typeof recordPatientPaymentApi>[0];
type CollectionResult = Awaited<ReturnType<typeof recordPatientPaymentApi>>;
const COLLECTION_CHANGED = 'money-collection-pending-changed';

/** Pending collection is saved before sending. Same-tab entry points share one
 * actor/org/permission-scoped intent. Never infer a duplicate from its amount. */
export class PendingCollection {
  constructor(readonly key: string, private storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>) {}
  read(): CollectionPayload | null {
    const raw = this.storage.getItem(this.key);
    if (raw === null) return null;
    const value: unknown = JSON.parse(raw);
    if (!value || typeof value !== 'object') throw new Error('Unconfirmed payment could not be read.');
    const p = value as CollectionPayload;
    if (typeof p.requestId !== 'string' || !p.requestId.trim() ||
        typeof p.patientId !== 'string' || !p.patientId.trim() ||
        !Number.isSafeInteger(p.clinicId) || p.clinicId <= 0 ||
        !Number.isSafeInteger(p.amountMinorUnits) || p.amountMinorUnits <= 0 ||
        (p.appointmentId != null && (!Number.isSafeInteger(p.appointmentId) || p.appointmentId <= 0)) ||
        typeof p.occurredAt !== 'string' || !Number.isFinite(Date.parse(p.occurredAt)) ||
        [p.method, p.category, p.referenceNumber,p.methodSubtype,p.methodOtherName].some(v => v != null && typeof v !== 'string')) {
      throw new Error('Unconfirmed payment could not be read.');
    }
    return { clinicId: p.clinicId, patientId: p.patientId, appointmentId: p.appointmentId ?? null,
      amountMinorUnits: p.amountMinorUnits, method: p.method ?? null, category: p.category ?? null,
      referenceNumber: p.referenceNumber ?? null, ...('methodSubtype' in p?{methodSubtype:p.methodSubtype}:{}), ...('methodOtherName' in p?{methodOtherName:p.methodOtherName}:{}), occurredAt: p.occurredAt, requestId: p.requestId,
      ...(p.allocationIntent ? {allocationIntent:p.allocationIntent,previewDigest:p.previewDigest}: {}) };
  }
  begin(payload: CollectionPayload): CollectionPayload {
    const old = this.read();
    if (old) {
      const identity = (p: CollectionPayload) => JSON.stringify([p.clinicId, p.patientId, p.appointmentId ?? null,
        p.amountMinorUnits, p.method ?? null, p.category ?? null, p.referenceNumber ?? null,p.methodSubtype??null,p.methodOtherName??null, p.requestId,p.allocationIntent,p.previewDigest]);
      if (identity(old) !== identity(payload)) throw new Error('Resolve the unconfirmed payment before recording another.');
      return old;
    }
    this.storage.setItem(this.key, JSON.stringify(payload));
    const saved = this.read();
    if (!saved || JSON.stringify(saved) !== JSON.stringify({ clinicId: payload.clinicId,
      patientId: payload.patientId, appointmentId: payload.appointmentId ?? null,
      amountMinorUnits: payload.amountMinorUnits, method: payload.method ?? null,
      category: payload.category ?? null, referenceNumber: payload.referenceNumber ?? null,
      ...('methodSubtype' in payload?{methodSubtype:payload.methodSubtype}:{}), ...('methodOtherName' in payload?{methodOtherName:payload.methodOtherName}:{}),
      occurredAt: payload.occurredAt, requestId: payload.requestId,
      ...(payload.allocationIntent ? {allocationIntent:payload.allocationIntent,previewDigest:payload.previewDigest}: {}) })) {
      throw new Error('Could not preserve this payment for recovery. Nothing was sent.');
    }
    return saved;
  }
  complete(requestId: string) {
    if (this.read()?.requestId === requestId) this.storage.removeItem(this.key);
  }
}

export function useCollectionRecovery(me: DashboardMe | null | undefined) {
  const [pending, setPending] = useState<CollectionPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const store = useRef<PendingCollection | null>(null);
  const inFlight = useRef(false);
  const [boundScope, setBoundScope] = useState<string | null>(null);
  const lastActor = useRef<string | null | undefined>(undefined);
  const [actorRevision, setActorRevision] = useState(0);
  const scope = JSON.stringify([me?.organizationId, me?.role, [...(me?.clinicIds ?? [])].sort((a, b) => a - b)]);
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const refresh = useCallback(() => {
    try { setPending(store.current?.read() ?? null); setError(null); }
    catch { setError('Unconfirmed payment is unavailable. Do not record it again.'); }
  }, []);
  useEffect(() => {
    let cancelled = false, sequence = 0, eventSeen = false;
    store.current = null; setBoundScope(null); setPending(null); setReady(false); setBusy(false); inFlight.current = false;
    const client = getSupabaseBrowserClient();
    const bind = async (actor: string | null) => {
      const ticket = ++sequence;
      if (!actor && !client && import.meta.env.DEV) {
        const token = await authBearer();
        if (token) {
          const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(token));
          actor = Array.from(new Uint8Array(digest), b => b.toString(16).padStart(2, '0')).join('');
        }
      }
      if (cancelled || ticket !== sequence) return;
      // Initial verification may finish after typing. A later actor/sign-out
      // transition is different: drafts must not survive that boundary.
      if (lastActor.current !== undefined && lastActor.current !== actor) {
        setActorRevision(value => value + 1);
      }
      lastActor.current = actor;
      const prefix = actor && me?.organizationId ? `payment-collection:${actor}:[${me.organizationId},` : null;
      const key = actor && prefix ? `payment-collection:${actor}:${scope}` : null;
      if (key && store.current?.key === key) return;
      store.current = null; setBoundScope(null); setPending(null); setReady(false); setError(null);
      inFlight.current = false; setBusy(false);
      try {
        if (!key || !prefix) return;
        // Preserve an unknown collection across sign-out or permission changes.
        // Another scope must neither see its details nor silently replace it.
        for (let index = 0; index < window.sessionStorage.length; index++) {
          const other = window.sessionStorage.key(index);
          if (other && other !== key && other.startsWith(prefix) && !other.includes(':method:')) {
            setError('An unconfirmed payment belongs to your previous access context. Restore that context before recording another payment.');
            return;
          }
        }
        store.current = new PendingCollection(key, window.sessionStorage);
        refresh(); setBoundScope(scope); setReady(true);
      } catch { setError('Payment recovery storage is unavailable. Nothing will be sent.'); }
    };
    if (client) void client.auth.getSession().then(({ data }) => { if (!cancelled && !eventSeen) void bind(data.session?.user.id ?? null); })
      .catch(() => { if (!cancelled) setError('Could not verify the current session.'); });
    else void bind(null);
    const subscription = client?.auth.onAuthStateChange((event, session) => {
      if (event !== 'TOKEN_REFRESHED') { eventSeen = true; void bind(session?.user.id ?? null); }
    }).data.subscription;
    window.addEventListener(COLLECTION_CHANGED, refresh);
    return () => { cancelled = true; store.current = null; subscription?.unsubscribe(); window.removeEventListener(COLLECTION_CHANGED, refresh); };
  }, [scope, refresh]);
  const submit = async (payload: CollectionPayload): Promise<CollectionResult | null> => {
    const owner = store.current, capturedScope = scope;
    if (!owner || !owner.key.endsWith(`:${scope}`) || inFlight.current) throw new Error('Payment recovery is not ready.');
    let intent: CollectionPayload;
    try { intent = owner.begin(payload); } // Persistence failure refuses BEFORE any request.
    catch (failure) {
      setError('Payment could not be prepared for safe recovery. No new request was sent.');
      throw failure;
    }
    inFlight.current = true; setBusy(true); refresh(); window.dispatchEvent(new Event(COLLECTION_CHANGED));
    try {
      const session = await moneyOperationSession('collection');
      if (owner.key !== `payment-collection:${session.actor}:${session.scopeKey}` ||
          store.current !== owner || currentScope.current !== capturedScope) {
        throw new Error('Your payment session changed. Recover the original request in its original session.');
      }
      await session.assertCurrent();
      const result = await recordPatientPaymentApi(intent, session.bearer);
      await session.assertCurrent();
      if (store.current !== owner || currentScope.current !== capturedScope) return null;
      if (result.payment.clinicId !== intent.clinicId || result.payment.patientId !== intent.patientId ||
          result.payment.amountMinorUnits !== intent.amountMinorUnits ||
          (result.payment.appointmentId ?? null) !== (intent.appointmentId ?? null) ||
          ('methodSubtype' in intent && (result.payment.method !== intent.method || (result.payment.methodSubtype??null)!==(intent.methodSubtype??null) || (result.payment.methodOtherName??null)!==(intent.methodOtherName??null) || (result.payment.referenceNumber??null)!==(intent.referenceNumber??null))) ||
          result.payment.currency !== 'INR' || !Number.isSafeInteger(result.payment.id) || result.payment.id <= 0) throw new Error('Payment result did not match this request.');
      try { owner.complete(intent.requestId); } catch { /* Keeping the same key is safe on reload. */ }
      refresh(); window.dispatchEvent(new Event(COLLECTION_CHANGED));
      return result;
    } catch (failure) {
      if (store.current !== owner || currentScope.current !== capturedScope) return null;
      // This route's validation errors happen before insertion. A transport
      // failure or conflict is NOT evidence of refusal and keeps its key.
      if (failure instanceof PortalApiError && ((failure.status === 400 && failure.code === 'VALIDATION_FAILED') || ['collection_preview_changed','bill_not_eligible','bill_allocation_unknown','merged_bill_identity_unresolved','PAYMENT_NOT_SAVED'].includes(failure.code??''))) {
        try { owner.complete(intent.requestId); } catch { /* Keep the safe pending key. */ }
        refresh(); window.dispatchEvent(new Event(COLLECTION_CHANGED));
      }
      setError(safeErrorMessage(failure, 'Payment is unconfirmed. Resume the same request.'));
      throw failure;
    } finally {
      if (store.current === owner) { inFlight.current = false; setBusy(false); }
    }
  };
  // Only the unsubmitted method choice is retained; no patient/payment values.
  // Reuse the verified actor/access binding, separate from financial recovery.
  const readMethodChoice = useCallback((context: string): string => {
    if (!ready || boundScope !== scope || !store.current) return '';
    try {
      const value = window.sessionStorage.getItem(`${store.current.key}:method:${context}`) ?? '';
      return (PAYMENT_METHOD_OPTIONS as readonly string[]).includes(value) ? value : '';
    } catch { return ''; }
  }, [ready, boundScope, scope, actorRevision]);
  const saveMethodChoice = useCallback((context: string, value: string): void => {
    if (!ready || boundScope !== scope || !store.current) return;
    try {
      const key = `${store.current.key}:method:${context}`;
      if ((PAYMENT_METHOD_OPTIONS as readonly string[]).includes(value)) window.sessionStorage.setItem(key, value);
      else window.sessionStorage.removeItem(key);
    } catch { /* A choice is not a submitted payment; recording still has its strict recovery guard. */ }
  }, [ready, boundScope, scope, actorRevision]);
  return { draftScope: JSON.stringify([scope, actorRevision]), pending: boundScope === scope ? pending : null, error, ready: ready && boundScope === scope, busy, submit, readMethodChoice, saveMethodChoice,
    retry: () => { const p = store.current?.read(); return p ? submit(p) : Promise.resolve(null); } };
}

/** Restore a saved method once per draft context; late binding cannot overwrite input. */
export function useCollectionMethodRestoration(
  recovery: ReturnType<typeof useCollectionRecovery>,
  context: string | null,
  restore: (method: string) => void,
): () => void {
  const draft = useRef<{ key: string; edited: boolean; restored: boolean } | null>(null);
  const key = JSON.stringify([recovery.draftScope, context]);
  const { ready, readMethodChoice } = recovery;
  useEffect(() => {
    if (draft.current?.key !== key) {
      draft.current = { key, edited: false, restored: false };
      restore('');
    }
    if (ready && context && !draft.current.restored) {
      if (!draft.current.edited) restore(readMethodChoice(context));
      draft.current.restored = true;
    }
  }, [key, context, ready, readMethodChoice, restore]);
  return useCallback(() => {
    if (draft.current?.key === key) draft.current.edited = true;
  }, [key]);
}

export function CollectionRecoveryPanel({ recovery, onRecovered, contextMatches, clinic }: {
  recovery: ReturnType<typeof useCollectionRecovery>; onRecovered: (result: CollectionResult) => void;
  contextMatches: boolean;
  clinic?: { readonly clinicId: number; readonly clinicLabel: string | null } | null;
}) {
  if (!recovery.pending && !recovery.error) return null;
  const pendingClinicName = clinic?.clinicId === recovery.pending?.clinicId
    ? clinic?.clinicLabel?.trim() || null : null;
  return <section className="ac-section" aria-label="Unconfirmed payment">
    <p role="status">{recovery.pending
      ? `Unconfirmed payment: ${formatMinorUnitsInr(recovery.pending.amountMinorUnits)} at ${pendingClinicName ?? 'clinic name unavailable'}. Resume this request before recording another payment.`
      : recovery.error}</p>
    {recovery.pending && !contextMatches && <Link reloadDocument to={`/dashboard/billing-entry?patientId=${encodeURIComponent(recovery.pending.patientId)}&clinicId=${recovery.pending.clinicId}${recovery.pending.appointmentId !== null ? `&appointmentId=${recovery.pending.appointmentId}` : ''}`}>Open the original payment context</Link>}
    {recovery.pending && contextMatches && <AcButton disabled={recovery.busy || !recovery.ready} onClick={() => {
      void recovery.retry().then(result => { if (result) onRecovered(result); }).catch(() => {});
    }}>{recovery.busy ? 'Checking payment...' : 'Resume payment'}</AcButton>}
    {recovery.pending && recovery.error && <p role="alert">{recovery.error}</p>}
  </section>;
}

export default function BillingEntryPage(): JSX.Element {
  const clinicCtx = useClinicLens();
  const meCtx = useDashboardMeContext();
  const collectionRecovery = useCollectionRecovery(meCtx?.me);
  const [detailsOpen,setDetailsOpen]=useState(false);
  const consultationScopeKey = useConsultationIdentity();
  const consultationContextRead = useRef(0);
  const consultationScope = useRef(consultationScopeKey);
  consultationScope.current = consultationScopeKey;
  const [context, setContext] = useState<BillingEntryContext | null>(null);
  const [contextError, setContextError] = useState<string | null>(null);
  // THE LENS OWNS "WHERE AM I" (119-CLINIC-OPERATING-SPINE-V1 Phase 3, gap G1).
  // Billing entry is OPERATIONAL -- money capture at the clinic you are working
  // in -- so it keeps no private clinic value and its own Clinic select is
  // gone. But it is NOT simply the lens: a bill opened from a day-board link
  // belongs to THAT APPOINTMENT'S clinic, and forcing it to the lens would
  // attach money to the wrong branch. So the value is the RECORD's clinic when
  // a record supplied one, else the LENS -- the same record-vs-lens split the
  // search card draws, never a private third choice.
  const [clinicId, setClinicId] = useState<number | null>(null);
  // TRUE when the clinic came from the opened RECORD rather than the lens.
  const [clinicFromRecord, setClinicFromRecord] = useState(false);
  // The clinic's NAME, from this page's own verified list. Never an id.
  const billingClinicLabel: string | null =
    clinicId === null
      ? null
      : clinicLabelOf(
          (context?.clinics ?? []).find((c) => c.clinicId === clinicId)
            ?.clinicLabel ?? null,
        );

  // TRAIN-B SCOPE 5: day-board prefill (parsed once on mount).

  const narrowCollection = useNarrowViewport('(max-width: 1099px)');
  const [prefill] = useState<BillingPrefill>(billingPrefillFromUrl);
  const [showPaymentHistory] = useState(() => new URLSearchParams(window.location.search).get('view') === 'history');
  const [linkedAppointmentId, setLinkedAppointmentId] = useState<number | null>(
    prefill.appointmentId,
  );
  const [prefillNote, setPrefillNote] = useState<string | null>(null);
  // FRONT-DESK-CORRECTIVE-V1 (L-02): did she arrive through ONE patient's
  // door? Read from the ARRIVAL, never from the live link, so removing a
  // link never re-shapes the page under her. Context was never lost
  // (WalkinApp.tsx:1074 -> :1893 here); the defect was COMPOSITION.
  const arrivedWithPatientContext = prefill.appointmentId !== null;
  const [advisedPlan, setAdvisedPlan] = useState<AdvisedPlanDisplay | null>(null);

  // MONEY-COMPLETION-V1 (V3-P3): the billing queue + the convert-moment
  // card + the post-payment receipt state.
  const [queueState, setQueueState] = useState<
    | { kind: 'loading' }
    | { kind: 'error' }
    | { kind: 'ready'; data: BillingQueueResponse }
    // FRONT-DESK-CORRECTIVE-V1 (L-02): the clinic-wide read was never made,
    // because this arrival is ONE patient's. An honest state, not a
    // 'loading' that never resolves. PAGE-LOCAL -- BillingQueueSectionProps
    // is untouched, so the section keeps its exact contract.
    | { kind: 'skipped' }
  >({ kind: 'loading' });
  const [queueRefresh, setQueueRefresh] = useState(0);
  const [episodeFacts, setEpisodeFacts] = useState<BillingQueueRow | null>(null);
  const [receipt, setReceipt] = useState<BillingReceipt | null>(null);
  // RECEPTION-OS-BUILD-V1 (Q-03/Q-DP-PRINT=B): the receipt PRINT swap (the
  // #442 ?print idiom -- a purpose-built chrome-free view; window.print).
  const [printingReceipt, setPrintingReceipt] = useState(false);
  const [receiptDocumentTitle,setReceiptDocumentTitle]=useState('Payment receipt');

  // BILLING-MISSING-DELTA-V1: the BILL print swap (same idiom, the bill
  // document instead of the payment receipt).
  const [printingBill, setPrintingBill] = useState<PatientBillWireMirror | null>(
    null,
  );

  // TREATMENT-ESTIMATE-ACCEPTANCE spine: the checkout handoff for the
  // linked episode. A failed admitted read stays visible and cannot bind
  // a patient or enable collection.
  const [handoff, setHandoff] = useState<CheckoutHandoffResponse | null>(null);
  const [handoffReadError, setHandoffReadError] = useState<{appointmentId:number;clinicId:number|null;message:string}|null>(null);

  const [searchQ, setSearchQ] = useState('');
  const [searching, setSearching] = useState(false);
  const [candidates, setCandidates] = useState<readonly PatientCandidateCard[] | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [patient, setPatient] = useState<SelectedPatient | null>(null);
  const collectArmSeq = useRef(0);

  // COLLECT-PAYMENT-V1 -- THE FIELD STARTS EMPTY, ON EVERY DOOR.
  // It used to be SEEDED from the Collect CTA's `suggestedAmount`, so a
  // receptionist who pressed Record without looking recorded a number
  // NOBODY HAD COUNTED. What is recorded here is money the clinic actually
  // received; the outstanding is a different fact, and offering it is a
  // different act. The offer now lives beside the field as an EXPLICIT
  // control ("Use outstanding amount"), reading the same derived figure --
  // one press away, never pre-committed.
  const [amountRaw, setAmountRaw] = useState('');
  const [method, setMethod] = useState('');
  const [methodSubtype,setMethodSubtype]=useState('');
  const [methodOtherName,setMethodOtherName]=useState('');
  const [category, setCategory] = useState('');
  // BILLING-MISSING-DELTA-V1 ("Other Payment Fields"): the optional payment
  // reference token (UPI ref / cheque no / txn id).
  const [referenceRaw, setReferenceRaw] = useState('');
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  const methodContext = patient && clinicId !== null ? JSON.stringify([clinicId, patient.patientId, linkedAppointmentId]) : null;
  const saveMethodChoice = collectionRecovery.saveMethodChoice;
  const restoreMethod = useCallback((value: string) => {
    setMethod(value);
    setMethodSubtype('');setMethodOtherName('');
    setReferenceRaw('');
  }, []);
  const markMethodEdited = useCollectionMethodRestoration(collectionRecovery, methodContext, restoreMethod);

  const billCollectionReview=useBillCollectionReview(clinicId,patient?.patientId??null,parseInrToMinorUnits(amountRaw),JSON.stringify([method,methodSubtype,methodOtherName,category,referenceRaw,linkedAppointmentId]),true);
  // SMART-PAYMENT-DRAWER-V1: the in-place collect drawer's armed episode
  // (null = closed). Deliberately SEPARATE from the page form's own state --
  // the drawer never reads or writes the page form, so the page's search,
  // history, void, unlinked and split behaviour is untouched.
  const [drawerAppointmentId, setDrawerAppointmentId] = useState<number | null>(
    null,
  );

  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<Feedback | null>(null);

  const [recent, setRecent] = useState<readonly PatientPaymentRow[]>([]);
  const [voidingId, setVoidingId] = useState<number | null>(null);
  const [reconciliationTarget,setReconciliationTarget]=useState<{paymentId:number;clinicId:number}|null>(null);
  const [pendingReconcile,setPendingReconcile]=useState<ReconciliationIntent|null>(null);
  const reconciliationRead = useRef(0);
  const refreshReconciliation=useCallback(()=>{
    const generation = ++reconciliationRead.current;
    const scope = consultationScope.current;
    const current = () => generation === reconciliationRead.current && scope === consultationScope.current;
    void pendingReconciliation().then(pending => {if(current())setPendingReconcile(pending);})
      .catch(error=>{if(current())setFeedback({kind:'error',text:safeErrorMessage(error,'Saved reconciliation could not be read.')});});
  },[]);
  useEffect(()=>{setReconciliationTarget(null);setPendingReconcile(null);refreshReconciliation();return()=>{reconciliationRead.current++;};},[refreshReconciliation,consultationScopeKey]);

  const [pendingVoidId,setPendingVoidId]=useState<number|null>(null);
  const [voidRecoveryReady,setVoidRecoveryReady]=useState(false);
  const [voidRecoveryError,setVoidRecoveryError]=useState<string|null>(null);
  const [voidBusy,setVoidBusy]=useState(false);
  const refreshVoidRecovery=useCallback(async () => {
    setVoidRecoveryReady(false);
    try {setPendingVoidId(await pendingPaymentVoidId());setVoidRecoveryError(null);setVoidRecoveryReady(true);}
    catch(error) {setVoidRecoveryError(safeErrorMessage(error,'Payment correction recovery is unavailable.'));}
  },[]);
  useEffect(() => {void refreshVoidRecovery();},[consultationScopeKey,refreshVoidRecovery]);


  useEffect(() => {
    let cancelled = false;
    const generation = ++consultationContextRead.current;
    setContext(null);
    setContextError(null);
    fetchBillingEntryContext()
      .then((ctx) => {
        if (cancelled || generation !== consultationContextRead.current) return;
        setContext(ctx);
        // TRAIN-B SCOPE 5: a day-board clinicId prefill wins when it is one
        // of this page's verified clinics; then F11 (STITCH-2 SCOPE 11): the
        // GLOBAL clinic context; then the context default. The page's own
        // select still narrows freely afterwards - all three are narrowing
        // WITHIN the server-verified clinic list, never authorization.
        const prefillValid =
          prefill.clinicId !== null &&
          ctx.clinics.some((c) => c.clinicId === prefill.clinicId);
        const globalPick = clinicCtx?.selectedClinicId ?? null;
        const globalValid =
          globalPick !== null &&
          ctx.clinics.some((c) => c.clinicId === globalPick);
        setClinicId(
          prefillValid
            ? prefill.clinicId
            : globalValid
              ? globalPick
              : ctx.defaultClinicId,
        );
        setClinicFromRecord(prefillValid);
      })
      .catch((err: unknown) => {
        if (cancelled || generation !== consultationContextRead.current) return;
        setContextError(safeErrorMessage(err, 'Failed to load'));
      });
    return () => {
      cancelled = true;
    };
  }, [consultationScopeKey, meCtx?.me]);

  // TRAIN-B SCOPE 5: prefill the patient from the day board's link via the
  // EXISTING patient-master record read (dark ladder / a bad id degrade to
  // the manual search with an honest note - never a fabricated selection).
  useEffect(() => {
    if (prefill.patientId === null || prefill.appointmentId !== null) return;
    let cancelled = false;
    fetchPatientMasterRecord(prefill.patientId)
      .then((res) => {
        if (cancelled || collectArmSeq.current !== 0 || res.patient.patientId !== prefill.patientId) return;
        setPatient({
          patientId: res.patient.patientId,
          fullName: res.patient.fullName,
          umr: res.patient.umr,
        });
      })
      .catch(() => {
        if (cancelled) return;
        setPrefillNote(
          'Could not prefill the patient from the day board - search below instead.',
        );
      });
    return () => {
      cancelled = true;
    };
  }, [prefill.patientId, prefill.appointmentId]);

  // DOCTOR-CLINICAL-WORKSPACE-V1 (Scope 6): surface the visit's advised
  // treatment as display-only payment context. Best-effort -- a failed read
  // renders nothing (the door works exactly as before; zero write-path
  // change; billing queue stays Train F).
  useEffect(() => {
    if (linkedAppointmentId === null) {
      setAdvisedPlan(null);
      return;
    }
    let cancelled = false;
    getCaseSheetByAppointment(linkedAppointmentId)
      .then((res) => {
        if (cancelled) return;
        setAdvisedPlan(advisedPlanFromCaseSheet(res.caseSheet));
      })
      .catch(() => {
        if (cancelled) return;
        setAdvisedPlan(null);
      });
    return () => {
      cancelled = true;
    };
  }, [linkedAppointmentId]);

  const refreshRecent = useCallback((forClinicId: number | null) => {
    if (forClinicId === null) {
      setRecent([]);
      return;
    }
    fetchRecentPatientPayments(forClinicId)
      .then((res) => setRecent(res.payments))
      .catch(() => setRecent([]));
  }, []);

  useEffect(() => {
    refreshRecent(clinicId);
  }, [clinicId, refreshRecent]);

  // MONEY-COMPLETION-V1: the billing queue for the page's selected clinic
  // (fault-isolated -- a queue failure never blocks recording a payment).
  useEffect(() => {
    if (clinicId === null) return undefined;
    // Arrived through ONE patient's door: the clinic-wide queue is not what
    // she asked for and shows every other patient's money. Not fetched, not
    // merely hidden. The episode-narrowed read below is unaffected.
    if (arrivedWithPatientContext) {
      setQueueState({ kind: 'skipped' });
      return undefined;
    }
    let cancelled = false;
    setQueueState({ kind: 'loading' });
    fetchBillingQueue({ clinicId })
      .then((data) => {
        if (!cancelled) setQueueState({ kind: 'ready', data });
      })
      .catch(() => {
        if (!cancelled) setQueueState({ kind: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [clinicId, queueRefresh, arrivedWithPatientContext]);

  // The convert-moment card facts for the linked episode (queue Collect OR
  // the day-board door URL). Best-effort: a failed read renders no card;
  // the form works exactly as before.
  useEffect(() => {
    if (linkedAppointmentId === null) {
      setEpisodeFacts(null);
      return undefined;
    }
    let cancelled = false;
    fetchBillingQueue({ appointmentId: linkedAppointmentId })
      .then((res) => {
        if (cancelled) return;
        setEpisodeFacts(
          res.rows.find((r) => r.appointmentId === linkedAppointmentId) ?? null,
        );
      })
      .catch(() => {
        if (!cancelled) setEpisodeFacts(null);
      });
    return () => {
      cancelled = true;
    };
  }, [linkedAppointmentId, queueRefresh]);

  // TREATMENT-ESTIMATE-ACCEPTANCE spine: the ONE checkout-handoff read for
  // the linked episode (composes the same owners server-side).
  useEffect(() => {
    setHandoffReadError(null);
    if (linkedAppointmentId === null) {
      setHandoff(null);
      return undefined;
    }
    let cancelled = false;
    fetchCheckoutHandoff(linkedAppointmentId)
      .then((res) => {
        if (!cancelled) setHandoff(res);
      })
      .catch((error:unknown) => {
        if (cancelled) return;
        setHandoff(null);
        const message=error instanceof PortalApiError && error.status===401
          ? 'Your session has expired. Sign in again to load this visit.'
          : error instanceof PortalApiError && error.status===403
            ? 'You do not have permission to view this visit.'
            : error instanceof PortalApiError && [400,404].includes(error.status)
              ? 'Visit unavailable. It may not exist or may be outside your clinic access.'
              : 'Couldn’t load this visit. Refresh the page to try again.';
        setHandoffReadError({appointmentId:linkedAppointmentId,clinicId,message});
      });
    return () => {
      cancelled = true;
    };
  }, [linkedAppointmentId, clinicId, queueRefresh]);

  // The admitted checkout read owns the linked visit's identity. Requiring a
  // second patient-master lookup made lawful billing depend on a different
  // record entitlement and left the form unbound. Never hydrate from URL labels.
  useEffect(() => {
    if (linkedAppointmentId === null || handoff === null ||
        handoff.appointmentId !== linkedAppointmentId ||
        handoff.clinicId !== clinicId || handoff.patientId === null) return;
    setPatient({patientId: handoff.patientId,
      fullName: handoff.patientLabel ?? 'Patient name unavailable',
      umr: handoff.patientUmr ?? ''});
  }, [handoff, linkedAppointmentId, clinicId]);

  const categories = useMemo(() => {
    if (context === null || clinicId === null) return [] as readonly string[];
    return context.categoriesByClinic[String(clinicId)] ?? [];
  }, [context, clinicId]);

  const onSearch = useCallback(() => {
    const q = searchQ.trim();
    if (q.length < 2) {
      setSearchError('Type at least 2 characters');
      return;
    }
    setSearching(true);
    setSearchError(null);
    fetchPatientMasterSearch(q)
      .then((res) => {
        setSearching(false);
        setCandidates(res.candidates);
      })
      .catch((err: unknown) => {
        setSearching(false);
        setCandidates(null);
        setSearchError(
          safeErrorMessage(err, 'Patient search is not available yet'),
        );
      });
  }, [searchQ]);

  const resetAfterSuccess = useCallback(() => {
    // LAKSHMI-BILLING-CLOSURE-V1 (L-07, the second half): the PATIENT IS
    // RETAINED across a success, for the same reason the visit link is.
    //
    // This line used to be `setPatient(null)`. The 2026-09-03 ruling kept the
    // EPISODE across "Record another payment" but the person was still
    // dropped, so the second payment was refused outright -- "Pick a patient
    // first (search above)" -- on a screen still naming that patient in the
    // checkout handoff directly above the form, reached through a door
    // carrying `?patientId=` in the URL. Her Rs 500 / Rs 300 / Rs 200 case
    // could not complete in one flow: the first fix stopped the money going
    // to the wrong place, and left it unable to go anywhere.
    //
    // SWITCHING PATIENTS REMAINS A DELIBERATE ACT, and both of its doors are
    // untouched: the explicit `change` control on the selected-patient line,
    // and a queue Collect door arming a DIFFERENT episode (which clears the
    // pending patient first so a slow prefill cannot arm the wrong person).
    // Recording money is not one of those doors -- it never was meant to be.
    setCandidates(null);
    setSearchQ('');
    setAmountRaw('');
    setMethod('');setMethodSubtype('');setMethodOtherName('');
    setCategory('');
    setReferenceRaw('');
    setRequestId(crypto.randomUUID());
    // FRONT-DESK-CORRECTIVE-V1 (L-07, founder ruling 2026-09-03): the visit
    // link is RETAINED. A success is not an exit from the episode the
    // operator opened -- only an explicit operator act is (see
    // nextLinkedAppointment in billing-entry-view.ts). Clearing it here is
    // what wrote her second payment unallocated.
    setLinkedAppointmentId((current) =>
      nextLinkedAppointment(current, { kind: 'record-another' }),
    );
    setPrefillNote(null);
  }, []);

  // MONEY-COMPLETION-V1 (Scope 3f): "Record another payment" is the ONLY
  // way back to an editable form after a success -- an intentional new
  // action, never a silently re-armed form.
  const onRecordAnother = useCallback(() => {
    setReceipt(null);
    resetAfterSuccess();
  }, [resetAfterSuccess]);

  // MONEY-COMPLETION-V1 (Scope 3a/3d): a queue row's Collect door -- arms
  // the convert-moment card + the form with the row's OWN episode (the
  // #252 door contract: the obligation's exact source appointment, never a
  // re-pick). The suggested amount is the EXACT outstanding (string math --
  // paise preserved); a not-configured row seeds NO amount (never Rs 0).
  // RACE GUARDS (adversarial review S3/S4): arming is refused while a
  // submit is in flight (a mid-flight re-arm could orphan the in-flight
  // payment's failure feedback), and the async patient prefill applies
  // ONLY when its arming is still the CURRENT one -- rapid Collect clicks
  // can never marry row A's patient to row B's episode.
  const onQueueCollect = useCallback(
    (row: BillingQueueRow) => {
      if (submitting) return;
      collectArmSeq.current += 1;
      setReceipt(null);
      setFeedback(null);
      // EXPLICIT EXIT: the operator picked another episode's Collect door.
      setLinkedAppointmentId((current) =>
        nextLinkedAppointment(current, {
          kind: 'queue-collect',
          appointmentId: row.doorAppointmentId,
        }),
      );
      setRequestId(crypto.randomUUID());
      if (row.state === 'outstanding' && row.outstandingMinorUnits !== null) {
        // COLLECT-PAYMENT-V1: the door ARMS THE EPISODE and OFFERS the
        // amount -- it no longer assigns it. Same reason as the URL door
        // above: an amount the operator did not type is an amount nobody
        // counted. The offer renders beside the field.
        setAmountRaw('');
        setPrefillNote(
          `Appointment #${row.doorAppointmentId}: use the outstanding amount, or enter what you collected.`,
        );
      } else {
        setAmountRaw('');
        setPrefillNote(
          row.state === 'not_configured'
            ? `Appointment #${row.doorAppointmentId}: accepted amount not configured - enter what you collected.`
            : null,
        );
      }
      // The next admitted checkout response binds this exact visit. Clear
      // both old values immediately so delayed responses cannot pair two visits.
      setPatient(null);
      setHandoff(null);
    },
    [submitting],
  );

  // SMART-PAYMENT-DRAWER-V1: the queue row's PRIMARY door -- collect this
  // visit's payment IN PLACE, with no url change and no page-form arming.
  // Refused while a page submit is in flight (the same S3/S4 race guard the
  // arming door carries). The episode is the row's OWN doorAppointmentId
  // (LES-022 -- a door never re-picks its episode).
  const onQueueOpenDrawer = useCallback(
    (row: BillingQueueRow) => {
      if (submitting) return;
      setDrawerAppointmentId(row.doorAppointmentId);
    },
    [submitting],
  );

  const onSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setFeedback(null);
      if (clinicId === null) {
        setFeedback({ kind: 'error', text: 'Pick a clinic first.' });
        return;
      }
      if (linkedAppointmentId !== null && (handoff === null ||
          handoff.appointmentId !== linkedAppointmentId || handoff.clinicId !== clinicId ||
          handoff.patientId === null || handoff.patientId !== patient?.patientId ||
          handoff.canRecordPayment !== true)) {
        setFeedback({kind:'error',text: handoff?.patientId === null
          ? 'This visit has no verified patient link. Resolve the patient link before recording.'
          : 'The visit’s billing context is not ready. Reload it before recording.'});
        return;
      }
      if (patient === null) {
        setFeedback({ kind: 'error', text: 'Pick a patient first (search above).' });
        return;
      }
      const amountMinorUnits = parseInrToMinorUnits(amountRaw);
      if (amountMinorUnits === null) {
        setFeedback({
          kind: 'error',
          text: 'Enter a valid positive amount (up to 2 decimal places).',
        });
        return;
      }
      // R-10 (Method required-not-enforced): the form marks Method with a
      // required asterisk -- the mark is now TRUE at this surface. The
      // WRITER's contract keeps method optional (legacy NULL-method rows
      // stay lawful evidence); this is UI honesty, not a write-path change.
      const detailError=paymentMethodDetailsError({method,methodSubtype:methodSubtype||null,methodOtherName:methodOtherName.trim()||null,referenceNumber:method==='cash'?null:referenceNumberForWire(referenceRaw)});
      if(detailError){setFeedback({kind:'error',text:detailError});return;}
      if (!(PAYMENT_METHOD_OPTIONS as readonly string[]).includes(method)) {
        setFeedback({ kind: 'error', text: 'Pick a payment method.' });
        return;
      }
      if(!billCollectionReview.command){setFeedback({kind:'error',text:'Review the bill allocation before recording.'});return;}
      setSubmitting(true);
      collectionRecovery.submit({
        ...billCollectionReview.command,
        clinicId,
        patientId: patient.patientId,
        // The EXISTING optional visit link (org/clinic-fenced server-side);
        // null when this entry is not day-board-linked.
        appointmentId: linkedAppointmentId,
        amountMinorUnits,
        method: method === '' ? null : method,
        category: category === '' ? null : category,
        // "Other Payment Fields": the optional reference token; the server
        // validates the format and maps pre-apply to an honest 409.
        referenceNumber: method === 'cash' ? null : referenceNumberForWire(referenceRaw),
        methodSubtype:methodSubtype||null,methodOtherName:methodOtherName.trim()||null,
        // Kept for older API clients; the server stamps the recording time.
        occurredAt: new Date().toISOString(),
        requestId,
      })
        .then((res) => {
          setSubmitting(false);
          if (!res) return;
          if (methodContext) saveMethodChoice(methodContext, '');
          // MONEY-COMPLETION-V1 (Scope 3f): the editable form is REPLACED
          // by the structured receipt -- post-success the page never looks
          // still-ready-to-submit; a refresh/back cannot resubmit (the
          // form state is gone and the requestId was single-use).
          setReceipt({ payment: res.payment, created: res.created });
          setFeedback(null);
          resetAfterSuccess();
          refreshRecent(clinicId);
          setQueueRefresh((n) => n + 1);
        })
        .catch((err: unknown) => {
          setSubmitting(false);
          setFeedback({
            kind: 'error',
            text: safeErrorMessage(err, 'Failed to record payment'),
          });
        });
    },
    [
      clinicId,
      patient,
      linkedAppointmentId,
      handoff,
      amountRaw,
      method,methodSubtype,methodOtherName,
      category,
      referenceRaw,
      requestId,
      billCollectionReview,
      collectionRecovery,
      refreshRecent,
      resetAfterSuccess,
    ],
  );

  const onConfirmVoid = useCallback(
    (paymentId: number, reason: string) => {
      setVoidingId(null);
      setVoidBusy(true);
      voidPatientPaymentApi(paymentId, reason)
        .then(() => {
          setFeedback({ kind: 'success', text: 'Payment voided. Re-enter the correct amount if needed.' });
          refreshRecent(clinicId);
        })
        .catch((err: unknown) => {
          setFeedback({
            kind: 'error',
            text: safeErrorMessage(err, 'Failed to void payment'),
          });
          refreshRecent(clinicId);
        }).finally(() => {setVoidBusy(false);void refreshVoidRecovery();});
    },
    [clinicId, refreshRecent, refreshVoidRecovery],
  );

  const [openingReceiptId, setOpeningReceiptId] = useState<number | null>(null);
  const receiptReadGeneration = useRef(0);
  const [receiptReturnId, setReceiptReturnId] = useState<number | null>(null);
  const wasPrintingReceipt = useRef(false);
  useEffect(() => {
    if (wasPrintingReceipt.current && !printingReceipt) {
      document.querySelector<HTMLButtonElement>(receiptReturnId === null
        ? '[data-testid="billing-receipt-print"]' : `[data-testid="billing-open-receipt-${receiptReturnId}"]`)?.focus();
    }
    wasPrintingReceipt.current = printingReceipt;
    if (printingReceipt) {
      const frame = requestAnimationFrame(() => document.querySelector<HTMLElement>(
        '[data-testid="saved-receipt-panel"]')?.scrollIntoView({ block: 'start' }));
      return () => cancelAnimationFrame(frame);
    }
  }, [printingReceipt, receiptReturnId]);
  const receiptReadContext = useRef({ clinicId, scope: consultationScopeKey });
  receiptReadContext.current = { clinicId, scope: consultationScopeKey };
  useEffect(() => {
    receiptReadGeneration.current++;
    setOpeningReceiptId(null); setReceiptReturnId(null); setReceipt(null); setPrintingReceipt(false);
  }, [clinicId, consultationScopeKey]);
  const openSavedReceipt = async (payment: PatientPaymentRow) => {
    const generation = ++receiptReadGeneration.current;
    const captured = receiptReadContext.current;
    setOpeningReceiptId(payment.id); setReceiptReturnId(payment.id); setFeedback(null);
    try {
      const result = await fetchPatientReceipts({ patientId: payment.patientId, paymentId: payment.id });
      if (receiptReadGeneration.current !== generation || receiptReadContext.current.clinicId !== captured.clinicId ||
          receiptReadContext.current.scope !== captured.scope) return;
      const saved = result.payments.find(row => row.id === payment.id && row.patientId === payment.patientId && row.clinicId === payment.clinicId);
      if (!saved) throw new Error('Receipt not found in current scope');
      setReceipt({ payment: saved, created: false }); setPrintingReceipt(true);
    } catch (failure) {
      if (receiptReadGeneration.current === generation) setFeedback({ kind: 'error',
        text: safeErrorMessage(failure, 'Could not reopen this receipt. No payment was recorded.') });
    } finally {
      if (receiptReadGeneration.current === generation) setOpeningReceiptId(null);
    }
  };

  const allocationScope = useRef({ clinicId, organizationId: meCtx?.me?.organizationId });
  allocationScope.current = { clinicId, organizationId: meCtx?.me?.organizationId };
  const recovery = useAllocationRecovery(meCtx?.me?.organizationId ?? null, {
    committed: (payment, intent) => {
      if (allocationScope.current.clinicId !== intent.clinicId ||
          linkedAppointmentId !== intent.appointmentId ||
          (patient !== null && patient.patientId !== payment.patientId)) return;
      setRecent(rows => rows.map(row => row.id === payment.id ? payment : row));
      refreshRecent(intent.clinicId);
      setQueueRefresh(n => n + 1);
    },
    denied: isCurrent => {
      setContext(ctx => ctx ? { ...ctx, canVoid: false } : ctx);
      const org = allocationScope.current.organizationId;
      void fetchBillingEntryContext().then(ctx => {
        if (isCurrent() && allocationScope.current.organizationId === org) setContext(ctx);
      }).catch(() => { /* Keep capability closed when the refresh fails. */ });
    },
  });
  const allocationBlocked = !recovery.ready || (recovery.state !== null && recovery.state.phase !== 'saved');
  const onRelinkPayment = (paymentId: number) => {
    if (allocationBlocked || linkedAppointmentId === null || clinicId === null) return;
    void recovery.start({ kind: 'attach', paymentId, appointmentId: linkedAppointmentId, clinicId });
  };
  const onUnlinkPayment = (paymentId: number, appointmentId: number) => {
    if (allocationBlocked || context?.canVoid !== true || clinicId === null) return;
    void recovery.start({ kind: 'undo', paymentId, appointmentId, clinicId });
  };

  if (contextError !== null) {
    return (
      <div className="dashboard-page">
        {/* Standard 5 (rule 2 -- one page-header grammar). */}
        <PageHeader eyebrow="Front desk" title="Billing" />
        {/* V7-P4 adoption: the shared ErrorState -- WHAT + the domain
            message VERBATIM as the WHY (LES-009); audience wired from the
            real viewer role. */}
        <ErrorState
          title="Billing entry is not available"
          detail={contextError}
          audience={meCtx?.me?.role ?? null}
          testId="billing-context-error"
        />
      </div>
    );
  }

  // RECEPTION-OS-BUILD-V1 (Q-03/Q-DP-PRINT=B): the receipt print swap -- a
  // purpose-built chrome-free view over the ALREADY-COMMITTED payment (the
  // #442 print idiom promoted into the shared PrintSheet rail). Never a
  // re-fetch, never a write: printing cannot repeat the transaction.

  // BILLING-MISSING-DELTA-V1: the BILL print swap (the receipt idiom over
  // an already-read bill; no fetch, no write).
  if (printingBill !== null) {
    const printClinic =
      context?.clinics.find((c) => c.clinicId === printingBill.clinicId) ?? null;
    return (
      <BillPrintView
        bill={printingBill}
        clinicLabel={
          printClinic !== null
            ? clinicOptionLabel(printClinic)
            : `Clinic #${printingBill.clinicId}`
        }
        backLabel="Back to Collect payment"
        onBack={() => setPrintingBill(null)}
      />
    );
  }

  // COLLECT-PAYMENT-V1 -- IS THIS THE PATIENT COLLECTION SCREEN?
  // TRUE only when all of it is known: a selected patient, a named clinic, an
  // ARMED episode, and a checkout handoff describing THAT episode. Everything
  // the composition changes is gated on this ONE fact, so the clinic-wide
  // billing desk (search, unlinked entry, voids, history, the queue) renders
  // exactly as it did -- this is a composition for a patient's visit, not a
  // replacement for the page.
  /**
   * SWITCHING PATIENT -- ONE BEHAVIOUR, TWO DOORS.
   *
   * LAKSHMI-BILLING-CLOSURE-V1 (L-07, the switch fence). A VISIT BELONGS TO A
   * PATIENT, so releasing the patient releases the visit with them. The desk
   * control used to clear only the person and leave the previous patient's
   * episode ARMED -- so the next amount typed, for somebody else, carried the
   * first patient's appointment id. That is the money-on-the-wrong-visit class
   * L-07 is about, reached from the other side, and retaining the patient
   * across a success (this package's other half) makes the armed-episode
   * window longer, not shorter.
   *
   * Routed through `nextLinkedAppointment` with the EXISTING 'remove-link'
   * event rather than a bare setter, so there is still exactly one place that
   * decides what a link does -- and it is a NAMED FUNCTION rather than two
   * inline copies, because the composition's head now offers this same door
   * and two copies of a money fence is how the two drift apart.
   */
  const switchPatient = () => {
    setPatient(null);
    setLinkedAppointmentId((current) =>
      nextLinkedAppointment(current, { kind: 'remove-link' }),
    );
  };

  const collectComposition =
    handoff !== null &&
    patient !== null &&
    billingClinicLabel !== null &&
    linkedAppointmentId !== null &&
    handoff.appointmentId === linkedAppointmentId &&
    (receipt === null || (receipt.payment.appointmentId === linkedAppointmentId && receipt.payment.patientId === patient.patientId));

  const collectionView = linkedAppointmentId !== null && !showPaymentHistory;
  // Reuse the clinic history table while retaining the explicitly opened visit.
  // Dropping that identity made the existing allocation correction unreachable.
  const historyQuery = new URLSearchParams({ view: 'history' });
  if (clinicId !== null) historyQuery.set('clinicId', String(clinicId));
  if (linkedAppointmentId !== null) historyQuery.set('appointmentId', String(linkedAppointmentId));
  if (patient !== null) historyQuery.set('patientId', patient.patientId);
  const clinicHistoryHref = `/dashboard/billing-entry?${historyQuery}`;

  const receivedAtField = (
        <p className="ac-section__sub" data-testid="billing-occurred-zone">
          Received time is recorded automatically (IST).
        </p>
  );

  return (
    <div className="dashboard-page money-receipt-workspace" data-testid="billing-entry-page">
      <style>{`
        .money-receipt-workspace > .ac-masterdetail:not(.ac-masterdetail--single) { grid-template-columns: minmax(0, 1fr) minmax(340px, 440px); align-items: start; }
        .money-receipt-workspace [data-testid="saved-receipt-panel"] { scroll-margin-top: calc(var(--ac-topbar-h, 72px) + 24px); }
        .money-receipt-workspace .money-receipt-list { min-width: 0; }
        .money-receipt-workspace .billing-receipt__fields > div { grid-template-columns: minmax(7rem, 1fr) minmax(0, 2fr); gap: 8px; }
        .money-receipt-workspace .billing-receipt__fields dd { min-width: 0; overflow-wrap: anywhere; }
        .money-receipt-workspace > .ac-masterdetail > .ac-masterdetail__aside { min-width: 0; }
        @media screen and (max-width: 759px) { .money-receipt-workspace > .ac-masterdetail:not(.ac-masterdetail--single) { grid-template-columns: minmax(0, 1fr); } }
      `}</style>
      <MasterDetail single={!printingReceipt || receipt === null}>
      <div className="money-receipt-list">
      {/* Standard 5 (rule 2 + rule 10): the shared PageHeader grammar with a
          lawful back-path breadcrumb (Today -> Billing). The intro copy moves
          verbatim into the header subs (the 'book of record' vocabulary is
          preserved). */}
      <PageHeader
        eyebrow={undefined}
        title="Collect payment"
        breadcrumbs={
          collectComposition
            ? [
                { label: 'Today', to: BACK_TO_TODAY_PATH },
                { label: `Appointment #${linkedAppointmentId}` },
                { label: 'Collect payment' },
              ]
            : [{ label: 'Today', to: BACK_TO_TODAY_PATH }, { label: 'Collect payment' }]
        }

        testId="billing-page-header"
        subs={[]}
      />
      {/* MONEY-COMPLETION-V1 (V3-P3): the promised Train-F billing queue --
          the seam copy this section replaces said "arrives with Train F".
          FRONT-DESK-CORRECTIVE-V1 (L-02): CONDITIONAL, never deleted. When
          she arrived from ONE patient's drawer the page is that patient's;
          queue-first arrival (no appointmentId) still renders it in full. */}
      {arrivedWithPatientContext || queueState.kind === 'skipped' ? null : (
        <BillingQueueSection
          state={queueState}
          onCollect={onQueueCollect}
          onOpenDrawer={onQueueOpenDrawer}
        />
      )}

      {handoffReadError?.appointmentId===linkedAppointmentId && handoffReadError?.clinicId===clinicId ? <ErrorState title="Visit could not be loaded" detail={handoffReadError.message} testId="collection-visit-read-error"/> : null}
      <FeedbackBanner feedback={feedback} />

      {/* COLLECT-PAYMENT-V1 -- THE HEAD OF THE APPROVED COMPOSITION.
          Identity ONCE, and the three verified figures (accepted, collected,
          outstanding) together, above the form, in the first viewport. It
          renders only where all three are actually known: a selected patient,
          a checkout handoff for the ARMED episode, and a named clinic. When
          the handoff describes a different appointment than the one armed,
          this says nothing rather than attributing one visit's money to
          another (the same provenance fence the offer control carries).
          Everything below it is unchanged and still renders -- no capability
          of this page is removed by adding a head to it. */}
      {collectComposition && handoff !== null && patient !== null && billingClinicLabel !== null ? (
        <CollectPaymentSummary
          handoff={handoff}
          patientName={receipt !== null ? (receipt.payment.patientName ?? receipt.payment.patientId) : patient.fullName}
          patientRecordPath={receipt === null && meCtx?.me?.patientMasterEnabled === true && handoff.patientId !== null
            ? deskRecordHref(handoff.patientId, 'billing', `appointmentId=${handoff.appointmentId}`)
            : null}
          patientUmr={patient.umr === '' ? null : patient.umr}
          clinicLabel={billingClinicLabel}
          fromDayBoard={prefill.appointmentId === linkedAppointmentId}
          /* THE DOOR THE HIDDEN FIELDSET DEPENDS ON. `billing-group-who` is
             hidden on the composition with the comment "the head above ...
             carries the Change affordance" -- but this prop was never passed,
             so on the finished composition a patient could not be switched AT
             ALL: the only door was hidden and the promised one did not exist.
             Wiring it is the repair of that, not a new control; it is the same
             `switchPatient` the desk door uses, so the L-07 switch fence holds
             on both. */
          onChangePatient={receipt === null ? switchPatient : undefined}
          onRemoveVisitLink={receipt !== null ? undefined : () =>
            setLinkedAppointmentId((current) =>
              nextLinkedAppointment(current, { kind: 'remove-link' }),
            )
          }
        />
      ) : null}

      {/* COLLECT-PAYMENT-V1 -- THE DESKTOP ARRANGEMENT, at last reachable.
          The approved canvas puts the record-payment form LEFT and the visit
          details RIGHT at 1440; below 1100px everything is one column.

          THE FORM LEADS IN THE DOM, and the move is the point. The rail used
          to come first in source order; at 1440 a grid could place it right
          and it looked correct, but at 834 there is no grid, so the stack
          showed VISIT DETAILS and the itemised bill ABOVE the thing the
          operator opened this screen to do -- and the tab key walked them
          first at BOTH widths. The primary work now leads in the DOM at every
          width and the grid places the rail beside it where there is room.

          The clinic-wide billing desk gets NO class at all: two unclassed
          divs in normal block flow, so search, unlinked entry, voids,
          history, the queue and the drawer lay out as before. The one place
          the new order is visible there is a transient state -- a visit armed
          before its patient resolves. */}
      <div className={collectComposition ? 'ac-collect-grid quiet-payment-layout' : undefined}>
      <div className={collectComposition ? 'ac-collect__main' : undefined}>
      {receipt !== null ? (
        <div className="ac-section ac-collect__receipt">
        <BillingReceiptPanel
          receipt={receipt}
          recentPaymentsHref={collectionView ? clinicHistoryHref : '#billing-recent'}
          recentPaymentsLabel={collectionView ? 'All payments' : 'View in recent payments'}
          onRecordAnother={onRecordAnother}
          viewerRole={meCtx?.me?.role ?? null}
          me={meCtx?.me ?? null}
          dayBoardPath={meCtx?.me?.role === 'receptionist' ? '/walkin' : null}
          patientRecordPath={
            meCtx?.me?.patientMasterEnabled === true && receipt.payment.patientId
              ? // L-18: stamp the door, so Back on the record comes back here.
                deskRecordHref(receipt.payment.patientId, 'billing', `appointmentId=${receipt.payment.appointmentId}`)
              : null
          }
          onPrintReceipt={() => setPrintingReceipt(true)}
        />
        </div>
      ) : null}


      {/* COLLECT-PAYMENT-V1 -- FOLDED, not removed. On the composition the head
          already states the armed visit and carries its OWN Remove link
          (`collect-remove-link`), so this note is the same sentence a second
          time, two blocks below the first -- the duplication the approved
          composition exists to end, and the same argument that already
          suppressed ConvertMomentCard here. The note and its control are
          untouched for the clinic-wide desk, which has no head to fold into. */}
      {receipt === null && !collectComposition ? (
        <BillingVisitLinkNote
          linkedAppointmentId={linkedAppointmentId}
          onRemoveLink={() =>
            setLinkedAppointmentId((current) =>
              nextLinkedAppointment(current, { kind: 'remove-link' }),
            )
          }
        />
      ) : null}
      {receipt === null && prefillNote !== null ? (
        <p className="dashboard-page__note" role="status" data-testid="billing-prefill-note">
          {prefillNote}
        </p>
      ) : null}
      {receipt === null && advisedPlan !== null ? (
        <div
          className="dashboard-page__note"
          data-testid="billing-advised-plan"
        >
          <strong>Treatment advised at this visit</strong> (display only -
          record what was actually collected):{' '}
          {advisedPlan.items
            .map(
              (i) =>
                `${i.name}${i.value !== null ? ` (${formatMinorUnitsInr(i.value * 100)})` : ''}`,
            )
            .join(', ')}
          {advisedPlan.total > 0
            ? ` - estimated total ${formatMinorUnitsInr(advisedPlan.total * 100)}`
            : ''}
        </div>
      ) : null}

      <CollectionRecoveryPanel recovery={collectionRecovery}
        clinic={context?.clinics.find(c => c.clinicId === collectionRecovery.pending?.clinicId)}
        contextMatches={collectionRecovery.pending?.clinicId === clinicId &&
          collectionRecovery.pending?.patientId === patient?.patientId &&
          collectionRecovery.pending?.appointmentId === linkedAppointmentId}
        onRecovered={res => {
        setReceipt({ payment: res.payment, created: res.created });
        resetAfterSuccess(); refreshRecent(res.payment.clinicId); setQueueRefresh(n => n + 1);
      }} />
      {receipt === null && !collectionRecovery.pending ? (
      <form
        onSubmit={onSubmit}
        /* `billing-entry__form` is styled by no stylesheet either; on the
           composition the form is the PRIMARY WORK plane and takes the same
           shipped card class the rail and the bill panel use. */
        className={collectComposition ? 'billing-entry__form ac-section' : 'billing-entry__form'}
      >
        {/* STITCH-2 SCOPE 12: grouped fields with labels + required marks,
            in the order the front desk works (who -> payment). Presentation
            only -- zero write-path change. */}
        {/* COLLECT-PAYMENT-V1 -- IDENTITY ONCE. On the composition the head
            above already names the clinic, the patient and the UMR, and
            carries the Change affordance; repeating them here is the
            duplication the approved composition exists to remove. The
            fieldset is HIDDEN, not deleted: with no patient selected, or on
            the clinic-wide desk, it is the only way to pick one and it
            renders exactly as before. */}
        <fieldset
          className="billing-entry__group"
          data-testid="billing-group-who"
          hidden={collectComposition}
        >
          <legend className="ac-section__eyebrow">Patient</legend>
        {/* Phase 3 (G1): the page's own Clinic select is gone -- the clinic is
            the record's (when a bill was opened from one) or the lens's, and a
            third editable copy is how money lands on the wrong branch. When it
            came from the record it is stated as differing from the lens, in
            words, so the operator sees which branch this bill belongs to. */}
        {billingClinicLabel !== null ? (
          <ClinicScopeLabel
            label={billingClinicLabel}
            mode={clinicFromRecord ? 'parameter' : 'lens'}
            lensLabel={clinicCtx?.selectedLabel ?? null}
            testId="billing-clinic-select"
          />
        ) : null}

        <div className="billing-entry__patient">
          {patient === null ? (
            <>
              <label>
                Patient (search name / phone / UMR)
                <input
                  value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                  placeholder="Search patient"
                  data-testid="billing-patient-search"
                />
              </label>
              <AcButton variant="primary" onClick={onSearch} disabled={searching}>
                {searching ? 'Searching...' : 'Search'}
              </AcButton>
              {searchError !== null ? (
                <div className="ac-banner ac-banner--danger" role="alert">
                  {searchError}
                </div>
              ) : null}
              {candidates !== null ? (
                candidates.length === 0 ? (
                  <EmptyState
                    title="No matching patient found."
                    body={
                      <>
                        If this payment belongs to a lead, open that lead and
                        use &quot;Create patient from this lead&quot; -- it
                        links the patient and brings you back here. Otherwise
                        register the patient on the Patients surface. Payments
                        are only recorded against an existing patient record.
                      </>
                    }
                    testId="billing-no-candidates"
                  />
                ) : (
                  <ul className="billing-entry__candidates">
                    {candidates.map((c) => (
                      <li key={c.patientId}>
                        <AcButton
                          variant="secondary"
                          size="sm"
                          onClick={() =>
                            setPatient({
                              patientId: c.patientId,
                              fullName: c.fullName,
                              umr: c.umr,
                            })
                          }
                        >
                          {c.fullName} -- {c.umr}
                        </AcButton>
                      </li>
                    ))}
                  </ul>
                )
              ) : null}
            </>
          ) : (
            <div data-testid="billing-selected-patient">
              {/* LAKSHMI-BILLING-CLOSURE-V1: the UMR gets its own element so
                  the identity on THE SCREEN WHERE MONEY IS RECORDED can be
                  read exactly, not scraped out of the surrounding sentence.
                  The write fence compares this value with `===`. */}
              Patient: <strong data-testid="billing-selected-patient-name">
                {patient.fullName}
              </strong>{' '}
              (<span data-testid="billing-selected-patient-umr">{patient.umr}</span>){' '}
              <AcButton
                variant="ghost"
                size="sm"
                data-testid="billing-patient-change"
                onClick={switchPatient}
              >
                change
              </AcButton>
            </div>
          )}
        </div>
        </fieldset>

        <fieldset className="billing-entry__group" data-testid="billing-group-payment"
          disabled={submitting || clinicId === null || patient === null ||
            (linkedAppointmentId !== null && (handoff === null ||
              handoff.appointmentId !== linkedAppointmentId || handoff.clinicId !== clinicId ||
              handoff.patientId !== patient.patientId || handoff.canRecordPayment !== true))}>
          <legend className="ac-sr-only">
            {collectComposition ? 'Record payment' : 'Payment details'}
          </legend>

        <div className="ac-collect__amount">
        <label>
          <span>{'Amount (₹)'} <span className="billing-entry__req" aria-hidden="true">*</span></span>
          {/* MONEY-COMPLETION-V1 (rider R-10, F-E): a money input's
              placeholder is NEVER a number -- the retired hardcoded rupee
              example read as real money (grep-pinned). A real PREFILL
              renders as a VALUE with its provenance note below; an empty
              field carries a non-numeric hint only. */}
          <input
            value={amountRaw}
            onChange={(e) => setAmountRaw(e.target.value)}
            placeholder={collectComposition ? "Amount received" : "Amount collected, in ₹"}
            inputMode="decimal"
            data-testid="billing-amount-input"
          />
        </label>
        {/* RECEPTION-OS-BUILD-V1 (Q-13=B, the F-17 consultation half): the
            org-configured PER-CLINIC walk-in consultation fee as a one-tap
            fill of the SAME editable field (overwrite stays free; the R-10
            placeholder law holds -- this is a config VALUE, never an
            invented example). Renders ONLY when THIS clinic's fee is
            configured (seed {}: honest absence, no dead door). */}
        {(() => {
          // TOLERANT READ (the LES-057 boundary posture): a stale server /
          // mock wire may lack the Q-13 field entirely -- the chip must
          // degrade to honest absence, never crash the page render (the
          // PR #454 CI walk24-2 failure: undefined[clinicId] threw here).
          const fee =
            clinicId !== null
              ? context?.walkinConsultationFeeByClinic?.[String(clinicId)]
              : undefined;
          if (linkedAppointmentId !== null) return null;
          if (typeof fee !== 'number') return null;
          return (
            <div className="billing-entry__field">
              <AcButton
                variant="secondary"
                size="sm"
                onClick={() => setAmountRaw(walkinFeeInputValue(fee))}
                data-testid="billing-walkin-fee-chip"
              >
                Walk-in consultation - {formatMinorUnitsInr(fee)}
              </AcButton>
              <span className="billing-entry__label"> org setting</span>
            </div>
          );
        })()}



        </div>

        {/* RECEPTION-OS-BUILD-V1 (F-22, the method half): quick-pick chips
            -- one tap instead of a two-step select (click-reduction backlog
            row 6). Same vocabulary, same state, nothing pre-answered (F8).
            The CATEGORY-prefill half of F-22 needs the plan-item -> catalog
            category join, which this wire does not carry -- NAMED
            LIMITATION, deferred with the money carrier (run record). */}
        <div className="billing-entry__field ac-collect__method">
          <PaymentMethodSelector referenceRaw={referenceRaw} referenceTestId="billing-reference-input" onReferenceChange={value=>{markMethodEdited();setReferenceRaw(value);}} methodSubtype={methodSubtype} methodOtherName={methodOtherName} onDetailsChange={patch=>{markMethodEdited();if(patch.methodSubtype!==undefined)setMethodSubtype(patch.methodSubtype);if(patch.methodOtherName!==undefined)setMethodOtherName(patch.methodOtherName);}} method={method} disabled={submitting} onChange={value=>{markMethodEdited();setMethod(value);if(value==='cash')setReferenceRaw('');if(methodContext)saveMethodChoice(methodContext,value);}}/>
        </div>

        {categories.length > 0 ? (
          <label>
            Category
            <select value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">-</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {collectComposition ? categoryLabel(c) : c}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        </fieldset>
        {!collectComposition ? receivedAtField : null}
        <BillCollectionReview review={billCollectionReview} />

        {/* THE ACTION ROW. Cancel is on the canvas and is built here, on the
            COMPOSITION ONLY: the clinic-wide desk has no origin to cancel
            back to, so a control claiming one there would be a claim without
            a behaviour. Its target is not invented either -- it is the SAME
            constant the page's own breadcrumb already uses for "Today", so
            the two cannot drift apart. */}
        <div className={collectComposition ? 'ac-collect__actions' : undefined}>
          {collectComposition && !narrowCollection ? (
            <Link
              to={BACK_TO_TODAY_PATH}
              className={acButtonClass('secondary')}
              data-testid="billing-cancel"
            >
              Cancel
            </Link>
          ) : null}
          <AcButton type="submit" variant="primary" disabled={submitting || !collectionRecovery.ready || !billCollectionReview.command || paymentMethodDetailsError({method,methodSubtype:methodSubtype||null,methodOtherName:methodOtherName.trim()||null,referenceNumber:method==='cash'?null:referenceNumberForWire(referenceRaw)})!==null}>
            {submitting ? 'Recording...' : `Record payment${parseInrToMinorUnits(amountRaw) !== null ? ` · ${formatMinorUnitsInr(parseInrToMinorUnits(amountRaw)!)}` : ''}`}
          </AcButton>
          {collectComposition && narrowCollection ? (
            <Link
              to={BACK_TO_TODAY_PATH}
              className={acButtonClass('secondary')}
              data-testid="billing-cancel"
            >
              Cancel
            </Link>
          ) : null}
        </div>

      </form>
      ) : null}
      </div>
      <details className="quiet-payment-details" onToggle={e=>setDetailsOpen(e.currentTarget.open)}><summary>Treatment, bills &amp; history</summary><div className="quiet-payment-detail-content">
      {handoff !== null ? (
        <CheckoutHandoffPanel
          billActions={linkedAppointmentId !== null && clinicId !== null ? (
        <BillWorkPanel
          viewerRole={meCtx?.me?.role ?? null}
          consultationScopeKey={consultationScopeKey ? `${consultationScopeKey}:${clinicId}` : undefined}
          consultationHints={context?.consultationActionsByClinic?.[String(clinicId)]}
          reloadBillingContext={async () => {
            const key = consultationScope.current;
            const generation = ++consultationContextRead.current;
            setContext(previous => previous ? { ...previous, consultationActionsByClinic: {} } : previous);
            const [fresh, freshHandoff] = await Promise.all([
              fetchBillingEntryContext(),
              fetchCheckoutHandoff(linkedAppointmentId),
            ]);
            if (consultationScope.current !== key || generation !== consultationContextRead.current) throw new Error('Consultation context changed');
            setContext(fresh);
            setHandoff(freshHandoff);
          }}
          headerActionTarget={null}
          appointmentId={linkedAppointmentId}
          clinicId={clinicId}
          onPrintBill={setPrintingBill}
        />
      ) : null}
          viewerRole={meCtx?.me?.role}
          historyHref={clinicHistoryHref}
          detailsOpen={detailsOpen}
          detailScopeKey={collectionRecovery.draftScope}
          handoff={handoff}
          variant={collectComposition ? 'visit-details' : 'handoff'}
          onChanged={() => setQueueRefresh((n) => n + 1)}
          patientRecordPath={
            meCtx?.me?.patientMasterEnabled === true && handoff.patientId !== null
              ? deskRecordHref(handoff.patientId, 'billing', `appointmentId=${handoff.appointmentId}`)
              : null
          }
        />
      ) : null}

      {/* COLLECT-PAYMENT-V1: on the composition this card is a THIRD
          restatement of the patient, the visit, the accepted value and the
          collected value -- all four already stated once, above. Suppressed
          HERE ONLY; the clinic-wide desk still renders it. */}
      {receipt === null && episodeFacts !== null && !collectComposition ? (
        <ConvertMomentCard row={episodeFacts} />
      ) : null}



      {/* BILLING-MISSING-DELTA-V1: the itemized bill workspace for the
          LINKED visit (the tracker's appointment-billing cluster). Renders
          only with a visit link + a verified clinic -- billing a visit is
          episode-anchored (LES-022: the episode id arrives from the door,
          never re-picked here). */}






      </div></details></div>
      {collectionView ? <AllocationRecoveryPanel recovery={recovery} payment={recent.find(row => row.id === recovery.state?.intent.paymentId)} /> : null}
      {!collectionView ? <section className="ac-section ac-collect__recent" aria-labelledby="billing-recent">
      <div className="ac-section__head">
        <h2 id="billing-recent" className="ac-section__title">Recent payments at this clinic</h2>
        {collectComposition ? <span className="ac-section__sub">{billingClinicLabel}</span> : null}
      </div>
      <AllocationRecoveryPanel recovery={recovery} payment={recent.find(row => row.id === recovery.state?.intent.paymentId)} />
      <div className="ac-table-scroll" role="region" aria-label="Recent payments table" tabIndex={0}>
      {pendingVoidId!==null || voidRecoveryError ? <div role="status">
        <p>{voidRecoveryError ?? `Payment #${pendingVoidId} has an unconfirmed correction.`}</p>
        <AcButton disabled={voidBusy || !voidRecoveryReady} onClick={() => {
          setVoidBusy(true);
          void retryPendingPaymentVoid().then(() => {refreshRecent(clinicId);setFeedback({kind:'success',text:'The saved payment correction is confirmed.'});})
            .catch(error => setFeedback({kind:'error',text:safeErrorMessage(error,'The correction could not be confirmed.')}))
            .finally(() => {setVoidBusy(false);void refreshVoidRecovery();});
        }}>Check saved correction</AcButton>
      </div> : null}
      {pendingReconcile?<div role="status" className="ac-note">Payment #{pendingReconcile.paymentId} has an unconfirmed allocation correction.
        <AcButton variant="secondary" onClick={()=>setReconciliationTarget({paymentId:pendingReconcile.paymentId,clinicId:pendingReconcile.clinicId})}>Recover saved reconciliation</AcButton>
      </div>:null}
      <RecentPaymentsTable
        reconciliationByClinic={context?.reconciliationByClinic}
        onReconcile={Object.values(context?.reconciliationByClinic??{}).some(Boolean)?p=>setReconciliationTarget({paymentId:p.id,clinicId:p.clinicId}):undefined}
        onOpenReceipt={payment => { void openSavedReceipt(payment); }}
        openingReceiptId={openingReceiptId}
        selectedPatientIdentity={patient}
        readableMethodLabels
        payments={recent}
        canVoid={context?.canVoid === true && voidRecoveryReady && pendingVoidId===null && !voidBusy}
        voidingId={voidingId}
        onStartVoid={setVoidingId}
        onConfirmVoid={onConfirmVoid}
        onCancelVoid={() => setVoidingId(null)}
        preserveAttachmentStatus={recovery.state !== null && recovery.state.phase !== 'saved'}
        relinkAppointmentId={linkedAppointmentId}
        onRelink={allocationBlocked ? null : onRelinkPayment}
        // UI PERMISSION ALIGNED TO THE ENDPOINT'S: the undo endpoint reuses
        // requireBillingVoidAccess, so the control renders only for the same
        // principals -- absent, never disabled, for everyone else.
        onUnlink={!allocationBlocked && context?.canVoid === true ? onUnlinkPayment : null}
      />

      </div>
      </section> : null}

      {/* SMART-PAYMENT-DRAWER-V1: the in-place collect drawer over this page.
          It owns its OWN arming and requestId; the page form below is
          untouched, so search / history / voids / unlinked / split behave
          exactly as before. Committing refreshes the queue + recent list. */}
      {reconciliationTarget?<LegacyReconciliationDrawer {...reconciliationTarget}
        onClose={()=>{setReconciliationTarget(null);refreshReconciliation();}}
        onSaved={()=>{refreshReconciliation();refreshRecent(clinicId);setQueueRefresh(n=>n+1);}}/>:null}
      {drawerAppointmentId !== null ? (
        <PaymentDrawer
          appointmentId={drawerAppointmentId}
          onClose={() => setDrawerAppointmentId(null)}
          onRecorded={() => {
            setQueueRefresh((n) => n + 1);
            refreshRecent(clinicId);
          }}
          fullBillingHref={`/dashboard/billing-entry?appointmentId=${drawerAppointmentId}${
            clinicId !== null ? `&clinicId=${clinicId}` : ''
          }`}
          dayBoardPath={meCtx?.me?.role === 'receptionist' ? '/walkin' : null}
          me={meCtx?.me ?? null}
        />
      ) : null}
      </div>
      {printingReceipt && receipt ? <ModalShell size="lg" trapFocus childHeaderActions title={receiptDocumentTitle} onClose={() => setPrintingReceipt(false)} testId="saved-receipt-panel">
          <ReceiptPrintView onDocumentTitleChange={setReceiptDocumentTitle} payment={receipt.payment}
            clinicLabel={context?.clinics.find(c => c.clinicId === receipt.payment.clinicId)?.clinicLabel ?? `Clinic #${receipt.payment.clinicId}`}
            backLabel="Back to Collect payment" onBack={() => setPrintingReceipt(false)} />
      </ModalShell> : null}
      </MasterDetail>
    </div>
  );
}
